/**
 * Twinkle Design Studio — rendered as blocks inside the theme's
 * main-product PDP (so media, title, price and variant picker match every
 * other product; see snippets/studio-designer-{chat,atc}.liquid).
 * - <studio-designer-chat>: chat with the Twinkle Bot via the app proxy
 *   (/apps/delivery-assignment/studio/*), unit/layout approvals, handoff.
 *   Product-neutral (ADR-0022): it boots from the `twinkle.studio_product`
 *   metafield slug, fetches the product config passthrough
 *   (…/studio/products/<slug>) and lets the config drive the flow —
 *   `hasLayoutPhase: false` products lock at the single unit approval, and
 *   the approve-layout piece count comes from the config's option-step map
 *   matched against the selected variant.
 * - <studio-designer-atc>: gated Add-to-Bag. Reads the selected variant
 *   from the theme variant picker (document `variant:change`) and stays
 *   disabled until the chat reports the design final-approved. The two are
 *   sibling blocks, so they coordinate via the document-level
 *   `studio-designer:status` event rather than a shared wrapper element.
 *   An unknown/misconfigured slug emits `studio-designer:fallback`: the
 *   widget hides and the theme's standard buy buttons are revealed, so the
 *   product page never dead-ends.
 *
 * Vanilla web components — matches the theme architecture (no framework).
 */
(function () {
  'use strict';

  var SESSION_KEY_PREFIX = 'twinkle:studio:session:';
  /* Composer text that hasn't been sent yet. The conversation itself lives on
     the server behind the session id, so it already survives a reload; this is
     only the half-typed message, which a language switch would otherwise eat. */
  var DRAFT_KEY_PREFIX = 'twinkle:studio:draft:';
  var POLL_INTERVAL_MS = 2000;
  /* Sorts after every ISO-8601 timestamp — what a chat row with no `createdAt`
     is worth when design cards are merged into the transcript by time. Only an
     optimistic bubble (typed, not yet acknowledged) is in that position, and it
     is by definition the newest thing on screen. */
  var LAST = '\uffff';
  /* The safety valve that stops a stuck "generating" card polling forever. It
     has to stay ABOVE the backend's own ceiling (STUDIO_TASK_TIMEOUT, 600 s),
     not below it: give up first and the customer is told their design failed
     while the worker is still rendering it, and the retry we offer them buys a
     second 4K render on top of the one already in flight.

     It used to be 150 ticks — 5 minutes — under a comment reading "generation
     should take seconds". That was true of a 1K chocolate tile. A backdrop is a
     4K render that the flat-artwork checker may reject and re-run up to three
     times (ADR-0026), so the honest worst case is minutes, and the widget was
     the first thing to give up on it. */
  var POLL_MAX_TICKS = Math.ceil(660000 / POLL_INTERVAL_MS);
  /* The inline proxy upload path must stay at or below this (4 MB — Vercel's
     serverless body limit 413s anything larger before the relay ever runs).
     Larger photos go via the direct-upload path (session.uploadUrl), which
     POSTs straight to the backend and bypasses the proxy (backend ADR-0021). */
  var MAX_PROXY_IMAGE_BYTES = 4 * 1024 * 1024;
  /* Hard client cap on any chosen photo — the direct path allows up to the
     backend's 50 MB; rejects a pathological pick before we try to upload it. */
  var MAX_DIRECT_IMAGE_BYTES = 50 * 1024 * 1024;
  /* Ceiling on a single direct upload. Sized for the worst realistic case
     (50 MB over a slow mobile link), not for a snappy connection — too tight a
     timeout would fail legitimate uploads. It exists so a half-open socket can
     never lock the composer indefinitely. */
  var UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

  /**
   * Substitute every occurrence of a `{{ token }}` in a locale string.
   *
   * split/join, not String.replace: replace with a string pattern swaps only
   * the FIRST match, and the piece-size copy uses `{{ cm }}` twice ("4×4 cm").
   */
  function fillToken(template, token, value) {
    return String(template).split(token).join(value);
  }

  /**
   * Print-spec millimetres → the centimetres shoppers actually think in
   * ("40" → "4", "35" → "3.5"). The spec stays mm because that is what the
   * vendor prints to; only the copy converts.
   */
  function formatCm(mm) {
    var cm = Number(mm) / 10;
    if (!isFinite(cm) || cm <= 0) return '';
    return String(Math.round(cm * 10) / 10);
  }

  /**
   * Is the shopper on the Arabic storefront? The `<html lang>` Shopify writes
   * per market — the widget has no locale of its own to consult from module
   * scope, and both custom elements already keyed off this.
   */
  function isArabicStorefront() {
    return (document.documentElement.lang || '').toLowerCase().indexOf('ar') === 0;
  }

  /**
   * ASCII digits -> Arabic-Indic, on the Arabic storefront only.
   *
   * Every other number on an Arabic PDP arrives already localised — Shopify
   * formats the price ("٧٥٠ ر.س") and Translate & Adapt carries the option
   * value ("٣ كيلو") — so a count this file injects as "180" was the one
   * Western number in the column and read as untranslated copy (reported
   * 2026-08-19). Presentation only: `normalizeOption` maps the digits back the
   * other way, so nothing that matches config against variants is fed from here.
   */
  function localizeDigits(value) {
    var text = String(value);
    if (!isArabicStorefront()) return text;
    return text.replace(/[0-9]/g, function (digit) {
      return String.fromCharCode(0x0660 + Number(digit));
    });
  }

  /**
   * Is this product sold BY THE PIECE (a tray of many), or is it one object?
   *
   * The piece-count and packaging sentences are written for a tray: "About 60
   * pieces", "Comes stacked in a Twinkle box". A cake resolves a quantity of 1
   * — one design, one cake — and got "About 1 pieces. Comes stacked…", which
   * is neither grammatical nor true; nothing about one cake is stacked
   * (reported 2026-08-12). One piece is also not a count worth stating, so the
   * same test settles both.
   */
  function isPieceCounted(pieceCount) {
    return typeof pieceCount === 'number' && pieceCount > 1;
  }

  /**
   * Is THIS PRODUCT sold by the piece (a tray of many), or is it one object?
   *
   * `quantity` on an option choice is the design-side piece count (PLAN D7),
   * never servings or layers. A cake's sizes are all 1 — one design, one cake.
   * Any choice above 1 means a tray. Matches the CX-pilot `productIsPieceCounted`.
   *
   * A config may carry NO quantities in its steps at all — the engine's
   * documented fallback for that is `defaultQuantity` (the same rule
   * `pieceCountForSelection` applies), so a steps-less tray keeps its facts
   * line. All-ones steps are a cake: the choices decided, and a stray
   * default must not overrule them.
   */
  function productIsPieceCounted(product) {
    var steps = (product && product.optionSteps) || [];
    var sawQuantity = false;
    for (var i = 0; i < steps.length; i += 1) {
      var choices = (steps[i] && steps[i].choices) || [];
      for (var j = 0; j < choices.length; j += 1) {
        if (typeof choices[j].quantity === 'number') sawQuantity = true;
        if (isPieceCounted(choices[j].quantity)) return true;
      }
    }
    if (!sawQuantity && product) return isPieceCounted(product.defaultQuantity);
    return false;
  }

  /**
   * What ships, as short sentences: how many pieces, how big, how it is boxed.
   *
   * Shared by the buy button's facts line and the approved card, because there
   * are TWO ways to buy — the card's button appears first, above the composer,
   * and the page's button sits below it. A shopper who takes the first one
   * never scrolls past it: "now I'm going to add to bag here, no one's going
   * to read below that". Whatever the second button says has to be said by the
   * first as well, and from one function so the two cannot drift apart.
   *
   * `counted` is whether the PRODUCT is sold by the piece (any amount-step
   * choice has quantity > 1). The selected size's own count still has to be
   * > 1 before we say anything piece-shaped — "About 1 pieces" is the cake bug
   * (reported 2026-08-12). `choiceNote` is the selected option's own
   * admin-authored line (a cake's "30 × 22 cm — serves about 10–12").
   *
   * The COUNT LEADS the line. It briefly did not: the size chips carry it too,
   * so it was suppressed here as a repeat (2026-08-24). It came back the next
   * day, and at the front — "move the count to start of sentence" — because
   * the chip is read once while choosing a size, and this line is what a
   * shopper re-reads with their thumb over the buy button. The count is the
   * fact they are checking; it should not arrive after two sentences about
   * centimetres and boxes. The chip repeating it is not a problem — the
   * duplicate that was worth removing was the one at the END of this same
   * line, and `choiceNoteForSelection` still drops that.
   *
   * The piece SIZE always stays — nothing above ever says how big 4×4 cm is.
   */
  function productFacts(t, pieceCount, pieceMm, choiceNote, counted, packagingNote) {
    var parts = [];
    if (counted) {
      if (isPieceCounted(pieceCount)) {
        parts.push(fillToken(t('atc_pieces'), '{{ count }}', localizeDigits(pieceCount)));
        if (pieceMm) {
          parts.push(fillToken(t('atc_piece_size'), '{{ cm }}', localizeDigits(formatCm(pieceMm))));
        }
      }
      /* The product's own packaging sentence when it states one, else the
         theme's. This is a claim about what physically ARRIVES — the factory
         moved from boxes to trays on 2026-09-07 — so it belongs to the
         product and has to be changeable without a theme deploy. Blank is
         the normal state for every product that has not set one, and keeps
         the storefront's wording exactly as it was. */
      parts.push(packagingNote || t('atc_packaging'));
    }
    if (choiceNote) parts.push(choiceNote);
    return parts;
  }

  /**
   * Suggestion labels that are a REQUEST FOR A HUMAN, not an answer to send.
   *
   * The bot is told to offer these exact words when it cannot make what was
   * asked for — a licensed character it refuses to draw, a prop we do not
   * build. Sent as chat text they read as the customer saying "talk to a
   * designer" TO the bot, which answers and carries on; mapped here they open
   * the handoff the words promise. Both storefront languages plus the common
   * Arabic phrasings, because the bot writes in the customer's language and
   * only the English wording is pinned in the prompt.
   */
  var HANDOFF_LABELS = [
    'talk to a designer',
    'talk to our design team',
    'كلم مصمم',
    'كلم مصممين',
    'تحدث مع مصمم',
    'التحدث مع مصمم',
    'تحدث مع فريق التصميم'
  ];

  /** Casefolded, whitespace-collapsed, Arabic diacritics and tatweel removed. */
  function normalizeLabel(value) {
    return String(value == null ? '' : value)
      .replace(/[\u064B-\u0652\u0640]/g, '')
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .join(' ');
  }

  function isHandoffLabel(label, localized) {
    var wanted = normalizeLabel(label);
    if (!wanted) return false;
    if (localized && wanted === normalizeLabel(localized)) return true;
    return HANDOFF_LABELS.indexOf(wanted) !== -1;
  }
  /**
   * Are two admin-authored lines the same sentence?
   *
   * Used to spot a size description that only repeats what its chip already
   * shows. Case, punctuation, spacing and digit script are all noise here: the
   * chip localises its digits (Arabic-Indic on the ar storefront) while a
   * hand-typed description may not, and "60 pieces." and "60 Pieces" are the
   * same claim. Compare what is left after all of that.
   */
  function sameSentence(a, b) {
    return normalizeSentence(a) === normalizeSentence(b) && normalizeSentence(a) !== '';
  }

  function normalizeSentence(value) {
    return String(value == null ? '' : value)
      .replace(/[\u0660-\u0669]/g, function (digit) {
        return String(digit.charCodeAt(0) - 0x0660);
      })
      .toLowerCase()
      .replace(/[^0-9a-z\u0600-\u06ff]+/g, ' ')
      .trim();
  }

  /** Mic is busy during the permission prompt AND while recording. */
  function isMicBusy(recording, pending) {
    return !!recording || !!pending;
  }

  /**
   * Is this a DWS product-config slug, or a translation of one?
   *
   * The slug is a SYSTEM IDENTIFIER carried by the `twinkle.studio_product`
   * metafield, and Shopify exposes every text metafield to translation apps
   * with no way to mark one untranslatable — per-field opt-out is an open
   * request (community.shopify.dev/t/add-the-concept-of-translatable-fields).
   * So Translate & Adapt's auto-translate rewrites it, and a rewritten slug
   * takes the whole widget down silently: the config lookup 404s and the PDP
   * quietly falls back to the plain buy button. It has happened twice — the
   * chocolate tray as "صينية الشوكولاتة" (2026-08-09) and the cake as
   * "كعكة مخصصة" (2026-08-17), each found only by a human switching to Arabic.
   *
   * Every slug this project issues is ASCII lowercase (`custom-cake`), so
   * anything else is a translation rather than a slug we can look up.
   */
  function looksLikeSlug(value) {
    return /^[a-z0-9][a-z0-9._-]*$/.test(String(value || ''));
  }

  /**
   * ``path`` with the locale prefix stripped, or '' when there is none.
   *
   * The untranslated slug is one request away: the same product page in the
   * store's DEFAULT language still renders it, because only the localised
   * storefront serves the translation. Shopify publishes the active prefix as
   * `Shopify.routes.root` ("/ar/" here, "/" on the default language), which is
   * what makes this a two-line lookup rather than locale guesswork. Returns ''
   * when we are already on the default language — there is nothing better to
   * ask for, and asking would just repeat the same answer.
   */
  function defaultLocalePath(path, localeRoot) {
    var root = String(localeRoot || '/');
    if (root === '/' || String(path || '').indexOf(root) !== 0) return '';
    return '/' + String(path).slice(root.length);
  }

  /** "48.2 MB" — keeps size copy in one place instead of hardcoded per locale. */
  function formatMegabytes(bytes) {
    var mb = bytes / (1024 * 1024);
    return (mb >= 10 ? Math.round(mb) : Math.round(mb * 10) / 10) + ' MB';
  }

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (e) {
      /* private mode — session simply won't survive reloads */
    }
  }

  function readJsonScript(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* One lookup per page even though two elements ask: the chat boots from the
     slug and the buy button stamps it onto the cart line as `_studio_product`,
     which the tools order webhook resolves the variant→quantity map from — a
     translated value would follow the design all the way into the order. */
  var slugLookups = {};

  /* Option values already complained about, so the warning below stays
     readable: `selectedChoice` is consulted on every render and every chat
     turn, and a console that repeats one line fifty times is a console nobody
     reads to the end. */
  var unresolvedChoiceWarnings = {};

  /**
   * The product-config slug as issued, recovering it when a translation app has
   * overwritten the metafield (see `looksLikeSlug`). Never rejects: on any
   * doubt it returns what the page gave us, which lands on today's behaviour.
   */
  function resolveStudioSlug(raw) {
    var value = raw || '';
    /* No metafield at all means "not a studio product", not a translation. */
    if (!value || looksLikeSlug(value)) return Promise.resolve(value);
    var routes = (window.Shopify && window.Shopify.routes) || {};
    var path = defaultLocalePath(window.location.pathname, routes.root);
    if (window.console && console.warn) {
      console.warn(
        '[studio-designer] the twinkle.studio_product metafield holds "' +
          value +
          '" rather than a config slug — a translation app has overwritten it. ' +
          'Clear that translation in Translate & Adapt' +
          (path ? '; recovering the slug from ' + path + ' meanwhile.' : '.')
      );
    }
    if (!path) return Promise.resolve(value);
    if (!slugLookups[value]) {
      slugLookups[value] = fetch(path, { credentials: 'same-origin' })
        .then(function (res) {
          return res.ok ? res.text() : '';
        })
        .then(function (html) {
          var match = /data-product-slug="([^"]*)"/.exec(html || '');
          var found = match ? match[1] : '';
          return looksLikeSlug(found) ? found : value;
        })
        .catch(function () {
          return value;
        });
    }
    return slugLookups[value];
  }

  /**
   * fetch wrapper: same-origin credentials, JSON-accepting, defensive against
   * the app proxy returning HTML error pages instead of JSON.
   * Resolves { status, data } where data is null when the body isn't JSON.
   */
  function apiFetch(url, options) {
    var opts = options || {};
    opts.credentials = 'same-origin';
    opts.headers = Object.assign({ Accept: 'application/json' }, opts.headers || {});
    return fetch(url, opts).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try {
          data = JSON.parse(text);
        } catch (e) {
          data = null;
        }
        return { status: response.status, data: data };
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Chat component                                                      */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('studio-designer-chat')) {
    customElements.define(
      'studio-designer-chat',
      class StudioDesignerChat extends HTMLElement {
        constructor() {
          super();
          this.session = null;
          /* The settled package we have already moved the picker to, so the
             move happens once and a shopper's own click afterwards stands.
             Per element, and Start Over builds a new session with no settled
             package, so it needs no explicit reset. */
          this.autoSwitchedFor = null;
          this.messages = [];
          this.assets = [];
          this.pendingAttachment = null;
          // The asset id is only meaningful for the session that minted it, so
          // it is stored WITH that session id and dropped on any mismatch.
          this.pendingAttachmentAssetId = null;
          this.pendingAttachmentSessionId = null;
          this.pendingAudio = null;
          this.activeUpload = null;
          this.uploadProgress = null;
          this.pollTimer = null;
          this.recordingTimer = null;
          this.recordingStartedAt = null;
          this.micPending = false;
          this.pollingAssetId = null;
          this.pollTimedOutAssetId = null;
          this.busy = false;
          this.mediaRecorder = null;
          this.recordedChunks = [];
          this.recording = false;
          /* Bumped by Start Over. Async turns capture it when they begin and
             discard their results when it moved: an abandoned send must not
             re-install the dead session, repaint its error over the fresh
             chat, or re-stage its attachment (the restart window spans a
             multi-minute upload, so this is not a theoretical race). */
          this.epoch = 0;
        }

        connectedCallback() {
          this.productId = this.dataset.productId || '';
          this.productSlug = this.dataset.productSlug || '';
          this.apiBase = this.dataset.apiBase || '/apps/delivery-assignment/studio';
          this.sessionKey = SESSION_KEY_PREFIX + this.productId;
          this.draftKey = DRAFT_KEY_PREFIX + this.productId;
          this.i18n = readJsonScript(document, '[data-studio-i18n]') || {};
          this.sizeGuides = readJsonScript(document, '[data-studio-size-guides]') || {};
          this.packageIcons = readJsonScript(document, '[data-studio-package-icons]') || {};
          this.locale = isArabicStorefront() ? 'ar' : 'en';
          this.loginUrl = this.dataset.loginUrl || '';
          this.product = null;
          /* Selected-variant tracking for the approve-layout piece count. */
          this.variants = readJsonScript(document, '[data-studio-variants]') || [];
          this.currentVariantId = null;
          var atcEl = document.querySelector('studio-designer-atc');
          if (atcEl && atcEl.dataset.initialVariantId) {
            this.currentVariantId = parseInt(atcEl.dataset.initialVariantId, 10) || null;
          }
          var self = this;
          this.onVariantChange = function (event) {
            if (event.detail && event.detail.variant) {
              self.currentVariantId = event.detail.variant.id;
              /* The piece count travels on the status event, so a size change
                 has to re-emit or the shopper sees the previous size's count. */
              /* emitStatus() re-stamps the picker too, which doubles as
                 insurance if the theme ever re-renders it for a variant
                 change. */
              self.emitStatus();
              /* Repainted rather than waiting for the next snapshot: the
                 shopper has just told us the size, and a picker that lags
                 behind the price they can see is the same
                 two-numbers-disagreeing problem the piece count below had.

                 No size warning goes with it any more. A size change used to
                 be able to strand approved designs (one per kilo, so 3 kg → 1
                 kg dropped two of them) and the customer was asked to confirm.
                 One tray carries one design now, so no size can cost them a
                 design and there is nothing to confirm. */
              self.renderSlots();
              /* The approved card quotes the piece count as well, and it is
                 built by renderActions() rather than by the status event — so
                 without this it kept saying "About 60 pieces" after the
                 shopper moved to 2 kg, right above a button that would have
                 charged them for 120. The page-level line updated and the card
                 did not, which is worse than neither updating: two numbers on
                 one screen disagreeing about the same order. */
              self.renderActions();
              /* And tell the SERVER, which otherwise never hears about it.
                 The package only ever reached the backend as a field on a
                 chat turn, so a shopper who moved this picker and did not
                 then type kept the mockup of the package they had left —
                 while the price, the size line and the buy button all
                 followed the switch, which is what made it look as though
                 only the picture was broken (reported 2026-09-01). */
              self.pushChosenOption();
            }
          };
          document.addEventListener('variant:change', this.onVariantChange);

          /* The buy block owns the date field and therefore the too-soon
             refusal, but the handoff — its modal, the phone field, the request
             it creates — lives here. An event rather than a cross-component
             method call, matching `studio-designer:add-to-bag` going the other
             way, so neither element has to hold a reference to the other. */
          this.onRushRequest = function (event) {
            self.confirmRushHandoff((event && event.detail && event.detail.eventDate) || '');
          };
          document.addEventListener('studio-designer:rush', this.onRushRequest);

          /* "Design another", from the buy block below. Same shape again: the
             button lives where the bag does, the session lives here. */
          this.onDesignAnother = function () {
            self.handleDesignAnother();
          };
          document.addEventListener('studio-designer:design-another', this.onDesignAnother);

          this.interceptStickyAtc();
          this.renderShell();
          resolveStudioSlug(this.productSlug).then(function (slug) {
            self.productSlug = slug;
            self.bootstrap();
          });
        }

        disconnectedCallback() {
          if (this.onRushRequest) {
            document.removeEventListener('studio-designer:rush', this.onRushRequest);
          }
          if (this.onDesignAnother) {
            document.removeEventListener('studio-designer:design-another', this.onDesignAnother);
          }
          this.stopPolling();
          this.stopRecordingTimer();
          this.micPending = false;
          this.stopRecorderTracks();
          this.cancelUpload();
          this.closeModal();
          if (this.onVariantChange) {
            document.removeEventListener('variant:change', this.onVariantChange);
          }
        }

        /**
         * Locale string lookup with the `{{ product }}` slot filled from the
         * loaded config (locale-appropriate name). Copy stays generic in the
         * theme locales; the product name is data (ADR-0022 / D2).
         */
        t(key) {
          var text = this.i18n[key] || key;
          if (text.indexOf('{{ product }}') !== -1) {
            text = fillToken(text, '{{ product }}', this.productName());
          }
          return text;
        }

        productName() {
          if (!this.product) return this.t('product_fallback_name');
          if (this.locale === 'ar' && this.product.nameAr) return this.product.nameAr;
          return this.product.name || this.t('product_fallback_name');
        }

        /* ---------------- product config (ADR-0022) ---------------- */

        /**
         * The config the whole flow keys off. Prefer the boot-time fetch;
         * fall back to the product block echoed on the session (covers a
         * transient config-fetch failure after a session was restored).
         */
        activeProduct() {
          return this.product || (this.session && this.session.product) || null;
        }

        hasLayoutPhase() {
          var product = this.activeProduct();
          /* Unknown config defaults to the two-phase flow — never skip an
             approval step just because a fetch failed. */
          return !product || product.hasLayoutPhase !== false;
        }

        /** Final, order-ready approval — the ATC gate and composer lock. */
        isFinalApproved(status) {
          if (status === 'LAYOUT_APPROVED') return true;
          return status === 'UNIT_APPROVED' && !this.hasLayoutPhase();
        }

        /**
         * The approve-layout piece count: the config's option-step map matched
         * against the selected variant's option values (D7 — quantities live in
         * the config, variants stay pricing truth). Falls back to the config
         * default. The order-time attach recomputes this server-side, so this
         * value only shapes the immediate print-sheet preview.
         */
        /**
         * Arabic-Indic digits → ASCII, lowercased, spaces stripped. Shopify
         * serves the TRANSLATED option values on a localised storefront, so a
         * merchant who writes "٢ كجم" in Translate & Adapt matches nothing
         * without this — and the piece count silently reverts to the default
         * (observed on the dev store, 2026-08-09).
         */
        normalizeOption(value) {
          return String(value)
            .replace(/[\u0660-\u0669]/g, function (d) {
              return String(d.charCodeAt(0) - 0x0660);
            })
            .toLowerCase()
            .replace(/\s+/g, '');
        }

        /** The leading number, which is the one part nobody can translate. */
        amountOf(value) {
          var match = this.normalizeOption(value).match(/(\d+(?:[.,]\d+)?)/);
          return match ? match[1].replace(',', '.') : '';
        }

        /** The selected variant's option values, normalised for matching. */
        optionSelection(variant) {
          var self = this;
          var options = (variant && variant.options) || [];
          return {
            normalized: options.map(function (option) { return self.normalizeOption(option); }),
            amounts: options
              .map(function (option) { return self.amountOf(option); })
              .filter(Boolean)
          };
        }

        /**
         * Does this config choice describe the selected variant? Text first
         * (value / label / label_ar), then the NUMBER alone — label_ar has to
         * agree with whatever the merchant typed in Shopify and nothing
         * enforces that, so text matching cannot be the only route.
         *
         * Shared by the piece count and the flavour note: two copies of this
         * drifted once already, and only the copy someone remembered to update
         * handled localised digits.
         */
        choiceMatches(choice, normalized, amounts) {
          var self = this;
          var texts = [choice.value, choice.label, choice.label_ar].filter(function (entry) {
            return typeof entry === 'string' && entry;
          });
          var hit = texts.some(function (entry) {
            return normalized.indexOf(self.normalizeOption(entry)) !== -1;
          });
          if (hit) return true;
          return texts.some(function (entry) {
            var amount = self.amountOf(entry);
            return amount && amounts.indexOf(amount) !== -1;
          });
        }

        /**
         * Annotate each size chip in the theme's variant picker with what that
         * size buys: the piece count for a tray ("60 pieces" under 1KG), or the
         * choice's own admin-authored line for a product that isn't counted in
         * pieces ("serves about 10–12" under a cake size).
         *
         * "Where's the information about the box?" — the count already sat in
         * the facts line by the price, but the shopper picks a SIZE first and
         * has nothing to choose between three weights on. The numbers come
         * from the same `quantity` map that sizes the print sheet, so the chip,
         * the facts line and the sheet cannot disagree; hardcoding them in
         * Liquid would have made a second source of truth for a number this
         * project has already watched drift once.
         *
         * Deliberately NOT done by renaming the Shopify option value: the
         * order webhook resolves piece counts by exact-matching that value
         * against the config (twinkle-tools `resolveVariantQuantity`), and a
         * renamed value silently drops every order to the default count.
         *
         * Choices with nothing to say (a flavour with no description, and every
         * non-studio product) are skipped, so this is inert everywhere but here.
         */
        decorateVariantPicker() {
          var self = this;
          var product = this.activeProduct();
          if (!product || !product.optionSteps) return;
          /* Scoped to THIS product's section, never the document. The studio
             template also mounts product-recommendations, a featured product
             and a collection slider, all of which render their own
             `.product-form__input` radios — and the choice matcher falls back
             to the leading number, so a featured cake sized "2 kg" would match
             the chocolate config's 2kg choice and get "120 pieces" stamped
             under it. A piece count for a different product is worse than no
             piece count at all. */
          var root = this.closest('[id^="shopify-section-"]') || document;
          var radios = root.querySelectorAll('.product-form__input input[type="radio"]');
          if (!radios.length) return;
          this.markSizeStep(root);
          (product.optionSteps || []).forEach(function (step) {
            if (!step || !step.choices) return;
            (step.choices || []).forEach(function (choice) {
              var note = self.chipNote(choice);
              if (note) self.stampChipNote(choice, note, radios, root);
              self.stampChipIcon(choice, radios, root);
            });
          });
        }

        /**
         * What this choice adds under its chip, in the shopper's language.
         *
         * A real piece count reads as "60 pieces"; one piece is not a count
         * ("1 pieces" under every cake size), so those products fall through to
         * the choice's own sentence.
         *
         * `size_label` wins over that sentence where a config offers one. The
         * founder's note on 2026-09-06 was that the sentences read as clutter
         * — "it makes the whole thing look messy, too much text" — and the
         * pictogram beside this note now carries what the package IS, leaving
         * the line to carry only how big it is. It is the same string the
         * finished mockup is badged with, and language-neutral by
         * construction, so it needs no `_ar` counterpart.
         */
        chipNote(choice) {
          if (!choice) return '';
          if (isPieceCounted(choice.quantity)) {
            return fillToken(this.t('picker_pieces'), '{{ count }}', localizeDigits(choice.quantity));
          }
          if (choice.size_label) return String(choice.size_label);
          var ar = isArabicStorefront();
          var text = ar ? choice.description_ar || choice.description : choice.description;
          return text ? String(text) : '';
        }

        /**
         * The pictogram under one chip: bare board, one garland, or two.
         *
         * Liquid owns the asset URLs, so the snippet hands over a map keyed by
         * option value and a product with no map simply shows no icons — which
         * is every studio product but the backdrop, exactly like the size
         * guides above.
         *
         * Decorative on purpose (`alt=""`, `aria-hidden`): the chip's own
         * label already names the package, and a screen reader that also reads
         * "board with balloons on both sides" has said the same thing twice.
         */
        stampChipIcon(choice, radios, root) {
          var self = this;
          var src = choice && choice.value ? this.packageIcons[choice.value] : '';
          if (typeof src !== 'string' || !src) return;
          Array.prototype.forEach.call(radios, function (radio) {
            var value = radio.value || '';
            if (!value) return;
            var amount = self.amountOf(value);
            if (!self.choiceMatches(choice, [self.normalizeOption(value)], amount ? [amount] : [])) {
              return;
            }
            var label = self.labelFor(radio, root);
            if (!label || label.querySelector('[data-studio-chip-icon]')) return;
            var img = document.createElement('img');
            img.className = 'studio-chip-icon';
            img.setAttribute('data-studio-chip-icon', '');
            img.src = src;
            img.alt = '';
            img.setAttribute('aria-hidden', 'true');
            img.setAttribute('loading', 'lazy');
            /* Intrinsic size, so the row does not reflow as the three load —
               the generator writes them all at one size. */
            img.setAttribute('width', '160');
            img.setAttribute('height', '196');
            label.appendChild(img);
            /* Tell the picker it now has icons hanging below it, so the CSS
               can reserve the room. Done here rather than with `:has()` in the
               stylesheet: `:has()` needs Safari 15.4+, and on anything older
               the whole rule is dropped and three absolutely-positioned icons
               land on whatever follows the picker — on a storefront that is
               overwhelmingly iOS. The JS already knows. */
            var picker = label.closest('.product-form__input');
            if (picker) picker.classList.add('studio-size-cards--iconed');
          });
        }

        /**
         * "Step 1" above the variant picker, so picking a size reads as the
         * step it is — the design that follows is made to fit the size, and the
         * chat is the step after it (its own heading carries "Step 2").
         *
         * Injected here rather than in Liquid because the picker is the shared
         * theme block: only a studio product ever reaches this code, so no
         * other PDP grows a step number.
         */
        markSizeStep(root) {
          var picker = root.querySelector('.product-form__input');
          if (!picker || !picker.parentNode) return;
          if (root.querySelector('[data-studio-step-lead]')) return;
          var lead = document.createElement('p');
          lead.className = 'studio-step-lead';
          lead.setAttribute('data-studio-step-lead', '');
          var step = document.createElement('span');
          step.className = 'studio-step-lead__step';
          step.textContent = this.t('step_one');
          lead.appendChild(step);
          /* Why size comes first, for products where the size IS the design
             surface. A tray's sizes are quantities and its chips already say
             so, so it stays quiet rather than explaining something else. */
          var product = this.activeProduct();
          var counted = productIsPieceCounted(product);
          if (!counted) {
            var hint = document.createElement('span');
            hint.className = 'studio-step-lead__hint';
            hint.textContent = this.t('step_size_hint');
            lead.appendChild(hint);
          }
          picker.parentNode.insertBefore(lead, picker);
        }

        /** The one chip this choice describes, annotated with `note`. */
        stampChipNote(choice, note, radios, root) {
          var self = this;
          Array.prototype.forEach.call(radios, function (radio) {
            var value = radio.value || '';
            if (!value) return;
            /* Same matcher the piece count itself uses — text first, then the
               bare number — so a chip and the facts line can never disagree
               about which choice the shopper picked. */
            var amount = self.amountOf(value);
            var matches = self.choiceMatches(
              choice,
              [self.normalizeOption(value)],
              amount ? [amount] : []
            );
            if (!matches) return;
            var label = self.labelFor(radio, root);
            if (!label) return;
            var span = label.querySelector('[data-studio-pieces]');
            if (span) {
              span.textContent = note;
              return;
            }
            span = document.createElement('span');
            span.className = 'studio-size-pieces';
            span.setAttribute('data-studio-pieces', '');
            span.textContent = note;
            label.appendChild(span);
          });
        }

        /**
         * The `<label>` driving a picker radio. `for` first; the sibling is the
         * fallback because an id carrying CSS-special characters cannot be put
         * through querySelector safely, and the picker renders label-after-input.
         */
        labelFor(radio, root) {
          if (radio.id) {
            var labels = (root || document).getElementsByTagName('label');
            for (var i = 0; i < labels.length; i += 1) {
              if (labels[i].htmlFor === radio.id) return labels[i];
            }
          }
          var next = radio.nextElementSibling;
          return next && next.tagName === 'LABEL' ? next : null;
        }

        pieceCountForSelection() {
          var self = this;
          var product = this.activeProduct();
          var fallback = (product && product.defaultQuantity > 0 && product.defaultQuantity) || 60;
          if (!product || !product.optionSteps) return fallback;
          var variant = null;
          var id = this.currentVariantId;
          (this.variants || []).forEach(function (candidate) {
            if (candidate.id === id) variant = candidate;
          });
          if (!variant) variant = this.variants[0] || null;
          var selection = this.optionSelection(variant);
          var normalized = selection.normalized;
          var amounts = selection.amounts;
          var found = null;
          (product.optionSteps || []).forEach(function (step) {
            if (found || !step || !step.choices) return;
            (step.choices || []).forEach(function (choice) {
              if (found || !choice || typeof choice.quantity !== 'number' || choice.quantity <= 0) return;
              /* label_ar too: Shopify localises option VALUES, so on the
                 Arabic storefront the variant's options arrive translated and
                 an English-only match silently drops to defaultQuantity —
                 a wrong piece count with nothing to notice it by. */
              if (self.choiceMatches(choice, normalized, amounts)) found = choice.quantity;
            });
          });
          return found || fallback;
        }

        /**
         * How many pieces buy ONE design — the smallest quantity any choice
         * offers, which for chocolate is the 1 kg tray.
         *
         * The twin of `ProductConfig.pieces_per_design()` on the server, and
         * derived the same way from the same `optionSteps`, so the two cannot
         * drift apart the way a duplicated constant would. 0 for products
         * whose choices declare no quantity (a backdrop is one board).
         */
        piecesPerDesign() {
          var product = this.activeProduct();
          var smallest = 0;
          ((product && product.optionSteps) || []).forEach(function (step) {
            ((step && step.choices) || []).forEach(function (choice) {
              if (!choice || typeof choice.quantity !== 'number' || choice.quantity <= 0) return;
              if (!smallest || choice.quantity < smallest) smallest = choice.quantity;
            });
          });
          return smallest;
        }

        /**
         * How many designs go on ONE order — one, whatever the size.
         *
         * USER, 2026-09-06: *"we agreed that each tray will have its own
         * design"*. The size buys more chocolate, not more designs; two
         * different designs means two trays, ordered separately. This used to
         * divide the piece count by the pieces one design is worth, which is
         * what produced a tray printed with two designs mixed together.
         *
         * Still a method, and still the only place the number is decided, so
         * the picker, the buy gate and the backend's `design_slot_count` can
         * be changed together if a product ever does carry two.
         */
        slotsForSelection() {
          return 1;
        }

        /**
         * Move the theme's variant picker onto ``variantId``.
         *
         * Positional, exactly as the theme reads its own picker: group N holds
         * option N. The ATC element does the same thing for its "switch to the
         * package we discussed" button — the duplication is deliberate rather
         * than a shared helper, because the two elements do not hold
         * references to each other by design, and a third module for twenty
         * lines of DOM would be the more expensive coupling.
         */
        selectVariant(variantId) {
          var record = null;
          (this.variants || []).forEach(function (candidate) {
            if (candidate && String(candidate.id) === String(variantId)) record = candidate;
          });
          if (!record || !(record.options || []).length) return;
          var root = this.closest('[id^="shopify-section-"]') || document;
          var picker = root.querySelector('variant-radios, variant-selects');
          if (!picker) return;
          var last = null;
          var groups = picker.querySelectorAll('fieldset');
          if (groups.length) {
            for (var i = 0; i < groups.length && i < record.options.length; i += 1) {
              var wanted = String(record.options[i]);
              var inputs = groups[i].querySelectorAll('input[type="radio"]');
              for (var j = 0; j < inputs.length; j += 1) {
                if (String(inputs[j].value) !== wanted) continue;
                if (!inputs[j].checked) {
                  inputs[j].checked = true;
                  last = inputs[j];
                }
                break;
              }
            }
          } else {
            var selects = picker.querySelectorAll('select');
            for (var n = 0; n < selects.length && n < record.options.length; n += 1) {
              var want = String(record.options[n]);
              if (selects[n].value === want) continue;
              var options = selects[n].options;
              var hit = false;
              for (var k = 0; k < options.length; k += 1) {
                if (String(options[k].value) === want) hit = true;
              }
              if (!hit) continue;
              selects[n].value = want;
              last = selects[n];
            }
          }
          /* One dispatch: the theme re-reads every fieldset on any change. */
          if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
        }

        /** The variant record the shopper currently has selected. */
        selectedVariantRecord() {
          var id = this.currentVariantId;
          var match = null;
          (this.variants || []).forEach(function (candidate) {
            if (candidate.id === id) match = candidate;
          });
          return match || (this.variants || [])[0] || null;
        }

        /**
         * The config choice the selected variant resolves to, or null.
         *
         * `{ key, value, label }` — the STEP key and the choice's canonical
         * value, which is what the backend stores and what the mockup is
         * anchored to. Sent with every chat turn (below) for a product whose
         * variant decides what physically stands in the photograph: a backdrop
         * rendered from the conversation alone can show balloons on both sides
         * while the cart holds the bare board, and nobody finds out until the
         * setup is standing at the party.
         */
        selectedChoice() {
          var self = this;
          var product = this.activeProduct();
          if (!product || !product.optionSteps) return null;
          var variant = this.selectedVariantRecord();
          if (!variant) return null;
          var selection = this.optionSelection(variant);
          var found = null;
          (product.optionSteps || []).forEach(function (step) {
            if (found || !step || !step.choices) return;
            (step.choices || []).forEach(function (choice) {
              if (found || !choice || !choice.value) return;
              if (self.choiceMatches(choice, selection.normalized, selection.amounts)) {
                found = {
                  key: String(step.key || ''),
                  value: String(choice.value),
                  label: self.choiceLabel(choice)
                };
              }
            });
          });
          if (!found) this.warnUnresolvedChoice(product, selection);
          return found;
        }

        /**
         * Say so, loudly, when a variant resolves to no choice at all.
         *
         * This function exists because the failure is otherwise completely
         * silent and completely invisible from the backend: the config is
         * well-formed, the Arabic is correct Arabic, every test passes, and
         * the only symptom on the page is an absence — no note under the
         * chips, no size-guide link, and no `selected_option` on the chat
         * turns (see `formData` below: the field is omitted rather than sent
         * empty, so the server's own label fallback never gets a chance).
         *
         * The backdrop shipped that way on 2026-08-26 and stayed that way for
         * ten days, on the Arabic storefront only, because Shopify's
         * translation of each option value drifted from the config's
         * `label_ar`. A person spotted it by eye. This is the line that should
         * have spotted it first.
         */
        warnUnresolvedChoice(product, selection) {
          if (!window.console || !console.warn) return;
          var raw = (selection.normalized || []).join(' / ');
          if (!raw || unresolvedChoiceWarnings[raw]) return;
          unresolvedChoiceWarnings[raw] = true;
          var offered = [];
          (product.optionSteps || []).forEach(function (step) {
            ((step && step.choices) || []).forEach(function (choice) {
              if (choice && choice.value) {
                offered.push(choice.value + ' (' + [choice.label, choice.label_ar].filter(Boolean).join(' / ') + ')');
              }
            });
          });
          console.warn(
            '[studio-designer] the selected variant "' +
              raw +
              '" matches no choice in the product config, so this product has no ' +
              'package: no size note, no size guide, and no tier sent with the design. ' +
              'The config offers ' +
              (offered.join('; ') || '(nothing)') +
              '. Almost always a Shopify option-value translation that no longer ' +
              'byte-matches label/label_ar — align the two in Translate & Adapt or in the ' +
              "product config's option steps."
          );
        }

        /**
         * The product's packaging sentence in the shopper's language, or ''.
         *
         * '' is the normal state: a product that states none leaves the
         * storefront's own wording in place, which is what every product did
         * before this field existed. Falls back to the English note on an
         * Arabic page rather than showing nothing, the same way `chipNote`
         * does — a sentence in the wrong language beats a missing fact about
         * what arrives.
         */
        packagingNoteForLocale() {
          var product = this.activeProduct() || {};
          var note = isArabicStorefront()
            ? product.packagingNoteAr || product.packagingNote
            : product.packagingNote;
          return note ? String(note) : '';
        }

        /** A choice as the shopper reads it, in the storefront's language. */
        choiceLabel(choice) {
          if (!choice) return '';
          var ar = isArabicStorefront();
          var label = ar ? choice.label_ar || choice.label : choice.label;
          return String(label || choice.value || '');
        }

        /**
         * The bot talking about one package while the cart holds another.
         *
         * Returns `{ discussed, selected }` in the shopper's language, or null
         * when they agree (or when either side has not settled). ONLY a
         * warning: the variant picker is the theme's, and quietly moving a
         * shopper's selection — or blocking a sale over a spec key the bot
         * guessed — would be worse than telling them what we noticed. The
         * server takes the same view: the cart wins when it renders the mockup.
         */
        /**
         * The package the BOT has settled, as a raw spec value ('' when none).
         *
         * Read across a turn to tell a change from a standing difference: only
         * a change moves the picker on its own.
         */
        specOptionValue() {
          var chosen = this.selectedChoice();
          if (!chosen || !chosen.key) return '';
          var spec = (this.session && this.session.spec) || {};
          var settled = spec[chosen.key];
          return typeof settled === 'string' ? settled.trim() : '';
        }

        optionMismatch() {
          var self = this;
          var product = this.activeProduct();
          var chosen = this.selectedChoice();
          if (!product || !chosen || !chosen.key) return null;
          var spec = (this.session && this.session.spec) || {};
          var settled = spec[chosen.key];
          if (typeof settled !== 'string' || !settled.trim()) return null;

          var discussed = null;
          (product.optionSteps || []).forEach(function (step) {
            if (discussed || !step || String(step.key || '') !== chosen.key) return;
            (step.choices || []).forEach(function (choice) {
              if (discussed || !choice) return;
              var names = [choice.value, choice.label, choice.label_ar];
              var hit = names.some(function (name) {
                return typeof name === 'string' && name && self.normalizeOption(name) ===
                  self.normalizeOption(settled);
              });
              if (hit) discussed = choice;
            });
          });
          /* A spec value naming nothing in the config is the bot's problem,
             not the shopper's — say nothing rather than warn about a package
             that does not exist. */
          if (!discussed || String(discussed.value) === chosen.value) return null;
          /* What switching would actually cost, so the buy block can offer it
             in one tap instead of only naming the disagreement. Null when the
             discussed package maps to no in-stock variant — the warning on its
             own is still true, and an offer that dead-ends is worse than none.

             Reported 2026-08-30: the bot asks which balloon package you want,
             you answer "one side", and the price under the button never moves
             off the board-only variant you happened to land on. Telling the
             shopper the two disagree was never the fix they wanted; acting on
             it is. */
          var target = this.variantForChoice(discussed);
          return {
            discussed: this.choiceLabel(discussed),
            selected: chosen.label,
            variantId: target ? target.id : null,
            /* The target's option VALUES, in the picker's own fieldset order —
               that ordering is the theme's contract (VariantRadios.updateOptions
               reads fieldsets positionally), so the switch can drive the real
               picker rather than reimplementing variant resolution. */
            options: target ? (target.options || []).slice() : [],
            price: target ? String(target.price_formatted || '') : '',
            /* Move the picker rather than offer a button — the design's
               package IS the product option (USER, 2026-08-31: "the option
               must be changed in the product option too"). A reload lands on
               the product's default, which left the shopper looking at a
               garland in the mockup above "Board only" selected in the
               options, and the bot settling a new package left the price on
               the old one.
               ONCE per settled value, which is what keeps it from fighting
               the shopper: after we have moved the picker to ONE_SIDE, a
               shopper who clicks back to Board only keeps their click and gets
               the warning instead. The next move takes a NEW settled value —
               the customer asking the bot for a different package. Tracking
               the value rather than who dispatched the event is deliberate:
               the theme fires `variant:change` during its own init, so "an
               event we did not cause" reads as a shopper click on every page
               load and would disable this entirely. */
            autoSwitch: this.autoSwitchedFor !== settled
          };
        }

        /**
         * The variant a config choice resolves to, or null.
         *
         * `selectedChoice()` walked the other way, through the same matcher, so
         * the "switch to what we discussed" offer can only ever land on a
         * variant the picker itself would have produced. Sold-out variants
         * count as no match: the shopper cannot buy one, and a switch that
         * greys the button out the moment it is taken helps nobody.
         */
        variantForChoice(choice) {
          var self = this;
          var found = null;
          (this.variants || []).forEach(function (candidate) {
            if (found || !candidate || !candidate.available) return;
            var selection = self.optionSelection(candidate);
            if (self.choiceMatches(choice, selection.normalized, selection.amounts)) {
              found = candidate;
            }
          });
          return found;
        }

        /**
         * The selected choice's own description, when the config carries one.
         *
         * Two jobs, one field. On a tray it describes the flavour — "what nuts?
         * Is there a known brand? I can't trust it if I haven't tried it": a
         * shopper cannot tell three flavours apart from three words. On a
         * product that isn't counted in pieces it carries the whole fact line
         * ("30 × 22 cm cake — serves about 10–12"), since the piece and
         * packaging sentences don't apply. Both are config edits, not releases.
         *
         * A description that only REPEATS ITS OWN CHIP is skipped. A
         * piece-counted choice's chip shows the count ("60 pieces"), and a
         * size description saying the same thing left the line under the
         * button ending in a bare repeat, "…serving dish not included. 60
         * pieces" (reported 2026-08-24). Only the repeat goes: a description
         * that says anything the chip does not — "180 pieces, serves about 20"
         * — is still shown here, because `chipNote` returns the count INSTEAD
         * of the description for these choices, so this line is the only place
         * that copy can appear. Dropping every piece-counted description would
         * have deleted admin-authored copy with nowhere left to read it.
         */
        choiceNoteForSelection() {
          var self = this;
          var product = this.activeProduct();
          if (!product || !product.optionSteps) return '';
          var variant = null;
          var id = this.currentVariantId;
          (this.variants || []).forEach(function (candidate) {
            if (candidate.id === id) variant = candidate;
          });
          if (!variant) variant = this.variants[0] || null;
          var selection = this.optionSelection(variant);
          var note = '';
          var ar = isArabicStorefront();
          (product.optionSteps || []).forEach(function (step) {
            if (note || !step || !step.choices) return;
            (step.choices || []).forEach(function (choice) {
              if (note || !choice) return;
              var text = ar ? choice.description_ar || choice.description : choice.description;
              if (!text) return;
              if (isPieceCounted(choice.quantity) && sameSentence(text, self.chipNote(choice))) return;
              if (self.choiceMatches(choice, selection.normalized, selection.amounts)) {
                note = String(text);
              }
            });
          });
          return note;
        }

        fetchProduct() {
          var self = this;
          if (!this.productSlug) return Promise.resolve(null);
          return apiFetch(this.apiBase + '/products/' + encodeURIComponent(this.productSlug)).then(function (res) {
            if (res.status === 200 && res.data && res.data.ok && res.data.product) {
              self.product = res.data.product;
              return self.product;
            }
            var err = self.errorFromResponse(res);
            throw err;
          });
        }

        /**
         * No usable config for this PDP (missing/unknown slug, backend 404):
         * hide the widget and reveal the theme's standard buy buttons — the
         * product must stay purchasable even when the studio is misconfigured.
         */
        renderFallback() {
          this.session = null;
          var block = this.closest('.studio-chat-block');
          if (block) block.hidden = true;
          document.dispatchEvent(new CustomEvent('studio-designer:fallback', { bubbles: true }));
          this.emitStatus();
        }

        /* ---------------- session helpers ---------------- */

        emitStatus() {
          var status = this.session ? this.session.status : null;
          /* Annotate the picker from here rather than from the config fetch:
             the config can also arrive on a restored session (activeProduct()'s
             fallback, after a transient fetch failure), and hanging it off the
             fetch alone left those shoppers with unlabelled size chips. Called
             on every state change and on variant:change; idempotent and it
             returns immediately when there is no config yet. */
          this.decorateVariantPicker();
          this.dispatchEvent(
            new CustomEvent('studio-designer:status', {
              bubbles: true,
              detail: {
                status: status,
                readyToOrder: !!status && this.isFinalApproved(status),
                productSlug: this.productSlug || null,
                sessionId: this.session ? this.session.id : null,
                previewAssetId: this.previewAssetId(),
                /* "I want more details about the product itself — how the
                   chocolates are distributed, piece size." The count is the
                   SAME number that sizes the print sheet, so what the shopper
                   is told and what gets printed cannot disagree.
                   NULL when the product config could not be loaded: the
                   fallback exists to keep the print sheet sane, not to be
                   quoted at a customer as fact. Better to say nothing than to
                   promise 60 pieces because a fetch failed. */
                pieceCount: this.activeProduct() ? this.pieceCountForSelection() : null,
                /* Physical piece size, straight from the vendor-accepted print
                   spec — the measurement that is actually produced. */
                pieceMm: (this.activeProduct() || {}).productMm || null,
                choiceNote: this.choiceNoteForSelection(),
                /* The buy button's copy has a `{{ product }}` slot too, and
                   only this element has the config to fill it. */
                productName: this.activeProduct() ? this.productName() : '',
                counted: productIsPieceCounted(this.activeProduct()),
                /* Days we need between the order and the customer's event; 0
                   for a product with no event date. The buy block renders its
                   date field from this, so the number the storefront enforces
                   is the same one the brief flags breaches of. */
                leadTimeDays: (this.activeProduct() || {}).leadTimeDays || 0,
                /* Resolved to the shopper's language HERE, where the product
                   config actually is — the buy block only ever sees this
                   event. Blank means the product states none and the widget
                   keeps its own wording. */
                packagingNote: this.packagingNoteForLocale(),
                /* The day that lead time counts FROM, as the store reckons it.
                   Not the browser's — see earliestEventDate(). '' when the
                   backend did not send one, which falls back to the browser. */
                shopToday: (this.activeProduct() || {}).shopToday || '',
                /* Bot said one package, cart holds another — see optionMismatch. */
                optionMismatch: this.optionMismatch()
              }
            })
          );
          /* Recorded AFTER the dispatch, so the buy block has the offer with
             `autoSwitch` still true and this run is the only one that acts. */
          var settled = this.specOptionValue();
          if (settled) this.autoSwitchedFor = settled;
        }

        /**
         * The composed tray for the approved design, or '' when there is none.
         *
         * READY only, newest first. Empty is the ordinary case for a product
         * with no blank photo or no zones — the card simply carries no image,
         * exactly as it did before.
         */
        approvedLayoutUrl() {
          var newest = null;
          (this.assets || []).forEach(function (asset) {
            if (asset.kind !== 'LAYOUT_PREVIEW' || asset.status !== 'READY') return;
            if (!newest || asset.createdAt >= newest.createdAt) newest = asset;
          });
          return newest ? this.assetUrl(newest.id) : '';
        }

        /** Best preview: LAYOUT_PREVIEW, else UNIT_PREVIEW, else UNIT_DESIGN. */
        previewAssetId() {
          var kinds = ['LAYOUT_PREVIEW', 'UNIT_PREVIEW', 'UNIT_DESIGN'];
          for (var i = 0; i < kinds.length; i += 1) {
            var asset = this.latestAssetOfKind(kinds[i]);
            if (asset && asset.status === 'READY') return asset.id;
          }
          return null;
        }

        latestAssetOfKind(kind) {
          var found = null;
          (this.assets || []).forEach(function (asset) {
            if (asset.kind === kind) found = asset;
          });
          return found;
        }

        assetUrl(assetId) {
          return this.apiBase + '/assets/' + encodeURIComponent(assetId);
        }

        /**
         * Proactively establish access on load (decision #17). ensureSession()
         * restores a stored session via snapshot, else creates one — anonymous
         * sessions are allowed unless the app demands login. The app (not
         * Liquid) is the gate, so we react to its response:
         *   - success               → render the chat + resume any polling
         *   - 401 login_required     → sign-in CTA
         *   - 403 feature_disabled   → hide the widget behind a notice
         *   - other/transient error  → render the chat shell so the user retries
         */
        bootstrap() {
          var self = this;
          if (!this.productSlug) {
            /* No metafield on this product — the widget has nothing to boot
               from; hand the PDP back to the standard buy buttons. */
            this.renderFallback();
            return;
          }
          this.fetchProduct()
            .then(function () {
              self.applyProductCopy();
              return self.ensureSession();
            })
            .then(function () {
              self.renderAll();
              self.emitStatus();
              self.resumePollingIfGenerating();
            })
            .catch(function (err) {
              if (err && err.status === 404) {
                /* Unknown / storefront-disabled slug — misconfiguration, not
                   an outage: fall back to a normal purchasable PDP. */
                self.renderFallback();
              } else if (err && err.status === 401) {
                self.renderLoginGate();
              } else if (err && err.status === 403) {
                self.renderDisabled();
              } else {
                /* Transient failure — don't hard-fail; let the user retry by
                   sending a message (which re-runs ensureSession()). */
                self.renderAll();
                self.emitStatus();
              }
            });
        }

        /** Fill the {{ product }}-slotted copy once the config names arrive. */
        applyProductCopy() {
          var block = this.closest('.studio-chat-block');
          var title = block && block.querySelector('[data-studio-block-title]');
          if (!title) return;
          title.textContent = '';
          /* A step chip ahead of the heading: designing comes AFTER picking a
             size ("the customer should select the size then chat with the
             bot"), and the picker above carries the matching "Step 1". Two
             chips are cheaper than a lock — the shopper is told the order
             rather than stopped, and Shopify has preselected a variant anyway.

             THREE on a product with a lead time, because the event date now
             sits between the picker and this heading as its own step (R7). A
             product without one — the chocolate tray — has no date field and
             stays at two, so the number is read from the config rather than
             written down. */
          var step = document.createElement('span');
          step.className = 'studio-chat-block__step';
          var dated = !!((this.activeProduct() || {}).leadTimeDays);
          step.textContent = this.t(dated ? 'step_three' : 'step_two');
          title.appendChild(step);
          title.appendChild(document.createTextNode(this.t('design_title')));
        }

        /** Resolves 'ok' | 'not_found' | 'error'. Mutates state on 'ok'. */
        fetchSnapshot(sessionId) {
          var self = this;
          return apiFetch(this.apiBase + '/sessions/' + encodeURIComponent(sessionId)).then(function (res) {
            if (res.status === 200 && res.data && res.data.ok) {
              var status = res.data.session && res.data.session.status;
              /* An EXPIRED session is not a session to resume — it is a dead
                 end, and resuming one is what made the widget open LOCKED.
                 The composer greys out (`syncComposerLock`), the quick replies
                 vanish (`quickReplies` runs for DRAFT only) and there is no
                 state card for EXPIRED, so the customer got a greeting, an
                 inert page and no explanation. The only ways out were Start
                 over — which nobody thinks to press on a chat they have not
                 started — and a second reload, because the handle below had
                 already been cleared. Reported 2026-09-01 with a screen
                 recording: "no matter what I clicked, nothing was working".
                 Its media has been scrubbed and its designs are gone, so there
                 is nothing here to show and nothing to say: drop the handle,
                 report it like a 404, and let `ensureSession` open a fresh
                 chat. A returning customer gets the working page they came
                 for. */
              if (status === 'EXPIRED') {
                storageSet(self.sessionKey, null);
                return 'not_found';
              }
              self.session = res.data.session;
              self.messages = res.data.messages || [];
              self.assets = res.data.assets || [];
              if (status === 'SENT_TO_DWS') {
                /* Terminal, but worth SHOWING: the order is placed and the
                   approved card says so. Drop the stored handle only, so the
                   next visitor on this device (shared computers, kiosks) does
                   not resume the previous customer's chat and photos, while
                   this view keeps working from the in-memory session. */
                storageSet(self.sessionKey, null);
              }
              return 'ok';
            }
            return res.status === 404 ? 'not_found' : 'error';
          }).catch(function () {
            return 'error';
          });
        }

        ensureSession() {
          if (this.session && this.session.id) return Promise.resolve(this.session);
          var self = this;
          var storedId = storageGet(this.sessionKey);
          var recover = storedId
            ? this.fetchSnapshot(storedId).then(function (result) {
                if (result === 'not_found') storageSet(self.sessionKey, null);
                return result === 'ok' ? self.session : null;
              })
            : Promise.resolve(null);
          return recover.then(function (session) {
            if (session) return session;
            return self.createSession();
          });
        }

        createSession() {
          var self = this;
          return apiFetch(this.apiBase + '/sessions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ locale: this.locale, product: this.productSlug })
          }).then(function (res) {
            if ((res.status === 201 || res.status === 200) && res.data && res.data.ok) {
              self.session = res.data.session;
              storageSet(self.sessionKey, self.session.id);
              return self.session;
            }
            throw self.errorFromResponse(res);
          });
        }

        errorFromResponse(res) {
          var err = new Error('api_error');
          err.status = res.status;
          err.code = res.data && res.data.error;
          err.serverMessage = res.data && res.data.message;
          return err;
        }

        errorText(err) {
          if (err && err.status === 401) return this.t('error_login');
          if (err && err.status === 409) return this.t('error_locked');
          if (err && err.status === 429) return this.t('error_cap');
          if (err && err.serverMessage) return err.serverMessage;
          return this.t('error_generic');
        }

        /* ---------------- rendering ---------------- */

        renderShell() {
          var frame = this.querySelector('[data-studio-chat-frame]') || this;
          frame.innerHTML =
            '<div class="studio-chat__header">' +
            '  <span class="studio-chat__bot-name"></span>' +
            /* A real button with the WhatsApp mark on it, not a text link:
               "a lot of people are telling us it's not clear what this is
               exactly... so it's clear that people are going to get taken to
               WhatsApp when they open it" (founder, 2026-09-06).

               A MONOCHROME glyph drawn here rather than `whatsapp-icon-svg`,
               which is the full-colour logo: that one is green-on-white and
               carries a gradient `id` that would be duplicated on any page
               also showing the floating WhatsApp button. This sits on a green
               fill and inherits `currentColor`. */
            '  <button type="button" class="studio-chat__handoff" data-studio-handoff>' +
            '    <svg class="studio-chat__handoff-icon" viewBox="0 0 24 24" width="18" height="18"' +
            '         fill="currentColor" aria-hidden="true" focusable="false">' +
            '      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38' +
            'c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2z' +
            'm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38' +
            'c0-4.54 3.7-8.23 8.25-8.23a8.19 8.19 0 0 1 5.82 2.42 8.16 8.16 0 0 1 2.41 5.82c0 4.54-3.7 8.23-8.23 8.23z' +
            'm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.79.98-.14.16-.29.18-.54.06' +
            '-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43' +
            '.12-.15.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43l-.48-.01' +
            'c-.16 0-.43.06-.65.31-.22.25-.85.83-.85 2.03s.87 2.35.99 2.51c.12.16 1.71 2.61 4.15 3.66.58.25 1.03.4 1.39.51' +
            '.58.19 1.11.16 1.53.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29z"/>' +
            '    </svg>' +
            '    <span class="studio-chat__handoff-label" data-studio-handoff-label></span>' +
            '  </button>' +
            '</div>' +
            /* The design picker. One slot per kilo the chosen size buys —
               so it appears on a 2 kg tray and not on a 1 kg one, and it
               re-reads the count on every snapshot because the shopper can
               change the variant at any time. Above the log rather than
               beside Add to bag: this is where the customer is looking while
               they design, and tapping a slot is a thing you do TO the
               conversation. */
            '<div class="studio-chat__slots" data-studio-slots hidden></div>' +
            '<div class="studio-chat__messages" data-studio-messages role="log" aria-live="polite"></div>' +
            '<div class="studio-chat__notice" data-studio-notice hidden></div>' +
            '<div class="studio-chat__actions" data-studio-actions></div>' +
            '<form class="studio-chat__composer" data-studio-composer>' +
            /* The attachment chip is a FULL-WIDTH row of the composer, ordered
               above the controls. It used to sit beside the text field, where
               its flex-shrink:0 stole 63% of the field the moment a photo was
               attached (T18). It must not sit inside the control row either —
               wrapping there leaves the attach button centred across two lines,
               orphaned from the field it belongs to. */
            /* Recording is a MODE, and until now the only thing that said so
               was the mic button's colour — which says nothing at all during
               the permission prompt, the longest part of a first recording.
               A full-width row above the controls, for the same reason the
               attachment chip is one. */
            '  <span class="studio-chat__recording" data-studio-recording role="status" aria-live="polite" hidden></span>' +
            '  <span class="studio-chat__attachment-chip" data-studio-chip hidden>' +
            '    <span class="studio-chat__chip-label" data-studio-chip-label></span>' +
            '    <button type="button" class="studio-chat__chip-clear" data-studio-chip-clear>&times;</button>' +
            '  </span>' +
            '  <button type="button" class="studio-chat__icon-button" data-studio-attach></button>' +
            '  <input type="file" accept="image/*" data-studio-file hidden>' +
            '  <input type="text" class="studio-chat__input" data-studio-input autocomplete="off">' +
            '  <button type="button" class="studio-chat__icon-button studio-chat__mic" data-studio-mic></button>' +
            '  <button type="submit" class="studio-chat__icon-button studio-chat__send" data-studio-send></button>' +
            '</form>' +
            '<div class="studio-chat__meta" data-studio-meta>' +
            '  <button type="button" class="studio-chat__meta-link" data-studio-privacy-link></button>' +
            '  <button type="button" class="studio-chat__meta-link studio-chat__meta-link--restart" data-studio-restart></button>' +
            '</div>';

          /* The disclosure / confirm dialog lives directly on the custom
             element — a sibling of the overflow:hidden frame — so it can
             overlay the whole widget while still inheriting the block's
             --studio-* custom properties. */
          var existingModal = this.querySelector('[data-studio-modal]');
          if (existingModal) existingModal.remove();
          var modal = document.createElement('div');
          modal.className = 'studio-chat__modal';
          modal.hidden = true;
          modal.setAttribute('data-studio-modal', '');
          modal.innerHTML =
            '<div class="studio-chat__modal-backdrop" data-studio-modal-backdrop></div>' +
            '<div class="studio-chat__modal-panel" role="dialog" aria-modal="true" tabindex="-1" data-studio-modal-panel>' +
            '  <h2 class="studio-chat__modal-title" data-studio-modal-title></h2>' +
            '  <p class="studio-chat__modal-body" data-studio-modal-body></p>' +
            '  <img class="studio-chat__modal-image" data-studio-modal-image alt="" hidden>' +
            '  <div class="studio-chat__modal-field" data-studio-modal-field hidden></div>' +
            '  <div class="studio-chat__button-row" data-studio-modal-actions></div>' +
            '</div>';
          this.appendChild(modal);

          this.els = {
            frame: frame,
            botName: frame.querySelector('.studio-chat__bot-name'),
            slots: frame.querySelector('[data-studio-slots]'),
            handoff: frame.querySelector('[data-studio-handoff]'),
            messages: frame.querySelector('[data-studio-messages]'),
            notice: frame.querySelector('[data-studio-notice]'),
            actions: frame.querySelector('[data-studio-actions]'),
            composer: frame.querySelector('[data-studio-composer]'),
            attach: frame.querySelector('[data-studio-attach]'),
            file: frame.querySelector('[data-studio-file]'),
            chip: frame.querySelector('[data-studio-chip]'),
            chipLabel: frame.querySelector('[data-studio-chip-label]'),
            chipClear: frame.querySelector('[data-studio-chip-clear]'),
            input: frame.querySelector('[data-studio-input]'),
            mic: frame.querySelector('[data-studio-mic]'),
            recording: frame.querySelector('[data-studio-recording]'),
            send: frame.querySelector('[data-studio-send]'),
            privacyLink: frame.querySelector('[data-studio-privacy-link]'),
            restart: frame.querySelector('[data-studio-restart]'),
            modal: modal,
            modalPanel: modal.querySelector('[data-studio-modal-panel]'),
            modalField: modal.querySelector('[data-studio-modal-field]'),
            modalTitle: modal.querySelector('[data-studio-modal-title]'),
            modalBody: modal.querySelector('[data-studio-modal-body]'),
            modalImage: modal.querySelector('[data-studio-modal-image]'),
            modalActions: modal.querySelector('[data-studio-modal-actions]'),
            modalBackdrop: modal.querySelector('[data-studio-modal-backdrop]')
          };

          /* Static SVG icon markup — no user content. */
          var ICONS = {
            /* A picture with a "+", not a camera: the button attaches an
               image the customer already has as often as it takes a new one,
               and a camera glyph read as "open the camera". USER, 2026-09-06. */
            addPhoto:
              '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 6H4.5A2.5 2.5 0 0 0 2 8.5v10A2.5 2.5 0 0 0 4.5 21h11a2.5 2.5 0 0 0 2.5-2.5V11"/><circle cx="7" cy="11.4" r="1.8"/><path d="m3.1 18.3 2.7-2.7a1.7 1.7 0 0 1 2.4 0l3.9 3.9"/><path d="m10.9 17 2.1-2.1a1.7 1.7 0 0 1 2.4 0l2.1 2.1"/><path d="M19.5 3.2v5.6M16.7 6h5.6"/></svg>',
            mic:
              '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3"/></svg>',
            send:
              '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M3.4 20.6 21.8 12 3.4 3.4l2.5 7.2 9.4 1.4-9.4 1.4-2.5 7.2Z"/></svg>'
          };

          this.els.botName.textContent = this.t('bot_name');
          /* The label span, not the button: the button also holds the
             WhatsApp glyph, and setting `textContent` on it would delete it. */
          var handoffLabel = this.els.handoff.querySelector('[data-studio-handoff-label]');
          if (handoffLabel) handoffLabel.textContent = this.t('talk_to_designer');
          this.els.input.placeholder = this.t('placeholder');
          this.els.input.setAttribute('aria-label', this.t('placeholder'));

          /* Restore here rather than in connectedCallback: renderShell() rebuilds
             the composer via innerHTML, so anything written before this point is
             thrown away with the old node. */
          var savedDraft = storageGet(this.draftKey);
          if (savedDraft && !this.els.input.value) this.els.input.value = savedDraft;
          var self = this;
          this.els.input.addEventListener('input', function () {
            storageSet(self.draftKey, self.els.input.value || null);
          });
          this.els.attach.setAttribute('aria-label', this.t('attach_image'));
          this.els.attach.innerHTML = ICONS.addPhoto;
          this.els.mic.setAttribute('aria-label', this.t('record_voice'));
          this.els.mic.innerHTML = ICONS.mic;
          this.els.send.setAttribute('aria-label', this.t('send'));
          this.els.send.innerHTML = ICONS.send;
          this.els.privacyLink.textContent = this.t('privacy_link');
          this.els.restart.textContent = this.t('start_over');
          this.els.chipClear.setAttribute('aria-label', this.t('clear_attachment'));

          if (!window.MediaRecorder || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.els.mic.hidden = true;
          }

          this.bindEvents();
        }

        bindEvents() {
          var self = this;
          this.els.composer.addEventListener('submit', function (event) {
            event.preventDefault();
            self.handleSend();
          });
          this.els.attach.addEventListener('click', function () {
            self.els.file.click();
          });
          this.els.file.addEventListener('change', function () {
            var file = self.els.file.files && self.els.file.files[0];
            if (!file) return;
            /* Gate against the cap that actually applies right now: 50 MB only
               when the direct path is live, otherwise the proxy's 4 MB. Gating
               on 50 MB unconditionally would accept a 6 MB photo and then
               reject it at send with "under 50 MB" — for a file under 50 MB. */
            var direct = self.canDirectUpload();
            var cap = direct ? MAX_DIRECT_IMAGE_BYTES : MAX_PROXY_IMAGE_BYTES;
            if (file.size > cap) {
              self.showNotice(self.sizeLimitText(direct, cap), 'error');
              self.els.file.value = '';
              return;
            }
            /* Clear the previous rejection. Without this the "pick an image
               under 4 MB" notice sat there next to the chip for the smaller
               photo the customer had just picked in response to it, so the
               fix looked like it had not worked (reported 2026-08-10). */
            self.showNotice('');
            self.setPendingAttachment(file);
          });
          this.els.chipClear.addEventListener('click', function () {
            self.clearPendingAttachment();
          });
          this.els.mic.addEventListener('click', function () {
            if (self.recording) self.stopRecording();
            else self.startRecording();
          });
          this.els.handoff.addEventListener('click', function () {
            self.confirmHandoff();
          });
          this.els.privacyLink.addEventListener('click', function () {
            self.openPrivacyModal();
          });
          this.els.restart.addEventListener('click', function () {
            self.confirmStartOver();
          });
          this.els.modalBackdrop.addEventListener('click', function () {
            self.closeModal();
          });
        }

        /**
         * Route the theme's sticky buy bar through the design gate.
         *
         * `layout/theme.liquid` renders <sticky-atc> on EVERY product, outside
         * this section — so the snippet's CSS, which hides the standard buy
         * button, never touches it. Its own handler clicks
         * `customization-product [type=submit]`: the hidden standard form the
         * studio template deliberately keeps for the theme's variant JS. On a
         * studio product that adds the variant with NO design session, NO
         * event date and NO preview link — an order that reaches fulfilment
         * with nothing attached, and no DWS request at all.
         *
         * Found on the dev store on 2026-08-19 by clicking it. The click is
         * swallowed and re-dispatched as the same request the approved card's
         * button makes, so every gate — approved design, event date — applies
         * to it.
         *
         * Bound on DOCUMENT in capture phase, matched with `closest()`, which
         * is the same shape `custom.js` uses for the city guard. Binding on
         * `.buy` itself looked equivalent and was not, for two reasons worth
         * writing down because both are easy to get wrong again:
         *
         *  1. At AT_TARGET the DOM runs capture and bubble listeners in
         *     REGISTRATION order and ignores the phase flag. `.buy` is an <a>
         *     wrapping a <span>, so a click on the text has `.buy` as an
         *     ancestor and capture wins — but a click on the <a>'s own padding
         *     targets the <a>, and then whoever registered first wins. That was
         *     us only because our deferred script sits above the sticky bar's
         *     in the document (`content_for_layout` is line 214 of theme.liquid,
         *     the sticky snippet is line 218). Moving one render, or either
         *     script becoming a module, would have silently reopened the hole.
         *  2. It required <sticky-atc> to already exist when this element
         *     connected. A document-level listener does not care when the bar
         *     is rendered, upgraded or replaced.
         *
         * The cost of binding this high is that we are now UPSTREAM of every
         * other guard on the page, and `custom.js` has one: its out-of-service
         * city check binds on `document.body`, also in capture, and its
         * selector list already includes `sticky-atc .buy`. Capture reaches
         * `document` before `document.body` whatever the registration order,
         * so this listener runs first — and `stopImmediatePropagation()` sets
         * the stop-propagation flag too, which meant the city guard never ran
         * on a studio product at all. Selecting an undeliverable city and
         * tapping the sticky bar took the shopper past a rule that applies to
         * every other product in the shop.
         *
         * Hence the two changes below:
         *
         *  - bail out when the theme has marked the button blocked, and let
         *    its own handler answer. `city-atc-blocked` is applied by
         *    `syncAddToCartAvailabilityByCity()` and is the theme's own signal.
         *  - `stopPropagation()`, not `stopImmediatePropagation()`. What we
         *    need is for `product-sticky-atc.js` not to click the hidden
         *    standard submit; we do not need every other listener in the page
         *    to stop existing.
         */
        interceptStickyAtc() {
          if (document.body.dataset.studioStickyBound) return;
          document.body.dataset.studioStickyBound = '1';
          document.addEventListener(
            'click',
            function (event) {
              var target = event.target;
              if (!target || !target.closest) return;
              if (!target.closest('sticky-atc .buy')) return;
              /* The city guard owns this click — leave it alone. Adding a
                 design gate must not remove a delivery-area one. */
              if (target.closest('.city-atc-blocked')) return;
              event.preventDefault();
              event.stopPropagation();
              document.dispatchEvent(
                new CustomEvent('studio-designer:add-to-bag', { bubbles: true })
              );
            },
            true
          );
        }

        /**
         * The size-guide diagram for the package in the cart, or ''.
         *
         * Liquid owns the asset URLs (the widget cannot build one), so the
         * snippet passes a {option value: url} map. No entry, no control —
         * every other studio product simply has none.
         */
        sizeGuideUrl() {
          var chosen = this.selectedChoice();
          if (!chosen) return '';
          var guides = this.sizeGuides || {};
          return typeof guides[chosen.value] === 'string' ? guides[chosen.value] : '';
        }

        /**
         * "How big is it?" — the one question the mockup cannot answer.
         *
         * The render shows the design at whatever size the screen is; this
         * shows the setup against a person, with the dimensions of the package
         * they are buying. A link rather than a second button on the card: the
         * card deliberately asks ONE question (see the approve row).
         */
        openSizeGuide() {
          var url = this.sizeGuideUrl();
          if (!url) return;
          this.openModal({
            title: this.t('size_guide_title'),
            body: this.t('size_guide_body'),
            image: url
          });
        }

        /* ---------------- modal (privacy disclosure + restart confirm) ---- */

        openModal(opts) {
          var self = this;
          this.modalReturnFocus = document.activeElement;
          var title = opts.title || '';
          this.els.modalTitle.textContent = title;
          this.els.modalTitle.hidden = !title;
          this.els.modalBody.textContent = opts.body || '';
          this.els.modalPanel.setAttribute('aria-label', title || opts.body || '');

          /* A picture, when the dialog is about one (the size guide). Its
             alt text is the body copy: the diagram carries dimensions a
             screen-reader user cannot see, and the sentence beneath it is
             where those numbers are written out. */
          var imageEl = this.els.modalImage;
          if (imageEl) {
            imageEl.hidden = !opts.image;
            imageEl.src = opts.image || '';
            imageEl.alt = opts.image ? opts.body || title || '' : '';
          }

          /* Optional single field (the handoff phone). Rebuilt per open so a
             cancelled dialog never leaks the previous answer into the next. */
          var fieldEl = this.els.modalField;
          fieldEl.innerHTML = '';
          fieldEl.hidden = !opts.field;
          var input = null;
          if (opts.field) {
            var label = document.createElement('label');
            label.className = 'studio-chat__modal-label';
            label.textContent = opts.field.label;
            input = document.createElement('input');
            input.type = opts.field.type || 'text';
            input.className = 'studio-chat__modal-input';
            input.autocomplete = opts.field.autocomplete || 'off';
            if (opts.field.placeholder) input.placeholder = opts.field.placeholder;
            label.appendChild(input);
            fieldEl.appendChild(label);
          }

          var actionsEl = this.els.modalActions;
          actionsEl.innerHTML = '';
          var buttons = opts.actions && opts.actions.length
            ? opts.actions
            : [{ label: this.t('close'), primary: true }];
          buttons.forEach(function (config) {
            var button = document.createElement('button');
            button.type = 'button';
            button.className =
              'button studio-chat__action-button ' +
              (config.primary ? 'button--primary' : 'button--secondary');
            button.textContent = config.label;
            button.addEventListener('click', function () {
              var value = input ? input.value.trim() : '';
              self.closeModal();
              if (config.onClick) config.onClick(value);
            });
            actionsEl.appendChild(button);
          });

          /* Enter in the field confirms — the input is not in a form, so
             without this the focused phone box swallows the keypress and the
             modal just sits there. Only field-bearing modals get it: the
             destructive restart confirm keeps Enter inert on purpose. */
          if (input) {
            input.addEventListener('keydown', function (event) {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              var primary = actionsEl.querySelector('.button--primary');
              if (primary) primary.click();
            });
          }

          this.els.modal.hidden = false;
          this._modalKeydown = function (event) {
            if (event.key === 'Escape') self.closeModal();
          };
          document.addEventListener('keydown', this._modalKeydown);
          /* Focus the last (safest — Close / Cancel) action so Enter/Escape
             never confirms a destructive choice by accident. */
          var focusTarget = input || actionsEl.querySelector('button:last-child');
          if (focusTarget) focusTarget.focus();
        }

        closeModal() {
          if (!this.els || this.els.modal.hidden) return;
          this.els.modal.hidden = true;
          if (this._modalKeydown) {
            document.removeEventListener('keydown', this._modalKeydown);
            this._modalKeydown = null;
          }
          if (this.modalReturnFocus && this.modalReturnFocus.focus) {
            this.modalReturnFocus.focus();
          }
          this.modalReturnFocus = null;
        }

        openPrivacyModal() {
          this.openModal({
            title: this.t('privacy_title'),
            body: this.t('privacy_notice')
          });
        }

        confirmStartOver() {
          var self = this;
          this.openModal({
            title: this.t('restart_title'),
            body: this.t('restart_body'),
            actions: [
              { label: this.t('start_over'), primary: true, onClick: function () { self.handleStartOver(); } },
              { label: this.t('cancel') }
            ]
          });
        }

        /**
         * Every route to "Talk to a designer" goes through here. The handoff is
         * not a cheap UI toggle: it creates a DesignRequest from the session,
         * attaches the designs, tags the Gallabox contact and notifies CS — and
         * it parks the bot for good, because a human now owns the conversation.
         * Customers were reaching all that with one accidental tap.
         */
        confirmHandoff() {
          var self = this;
          this.openModal({
            title: this.t('handoff_title'),
            body: this.t('handoff_body'),
            /* Ask for the number. Almost nobody signs in, so without this the
               shopper is anonymous, there is no Gallabox contact to attach the
               design to, and CX gets a thread they cannot tie to anything.
               Optional on purpose — a customer who will not give a number
               still gets the handoff and the wa.me link. */
            field: {
              label: self.t('handoff_phone_label'),
              type: 'tel',
              autocomplete: 'tel',
              placeholder: self.t('handoff_phone_placeholder')
            },
            actions: [
              {
                label: this.t('talk_to_designer'),
                primary: true,
                onClick: function (phone) { self.handleHandoff(phone); }
              },
              { label: this.t('cancel') }
            ]
          });
        }

        /**
         * "Need it sooner?" — the only way past a date we cannot build for.
         *
         * Ops removed rush orders entirely (2026-08-20) and answered "and if
         * needed then talk to the designer" (USER, 2026-08-23), so this is the
         * REAL handoff, not a bare WhatsApp link: it creates the DesignRequest
         * with the design, the transcript and the spec attached, which is what
         * whoever answers "can you build this in three days?" needs in front of
         * them. A cold message with none of that makes them rebuild it by hand.
         *
         * Confirmed first, and unlike `confirmHandoff` the safe action is not
         * "Cancel" but "Pick another date" — it takes them back to the field
         * and focuses it. This button sits on a DATE error, so the likeliest
         * shopper behind it does not want a human at all; they want a later
         * Saturday. Handing that person a parked session (B7.5: the handoff is
         * not reversible) for one tap would be the accidental-tap problem
         * `confirmHandoff` already exists to prevent, on the audience most
         * likely to hit it.
         */
        confirmRushHandoff(eventDate) {
          var self = this;
          this.openModal({
            title: this.t('rush_title'),
            body: this.t('rush_body'),
            field: {
              label: self.t('handoff_phone_label'),
              type: 'tel',
              autocomplete: 'tel',
              placeholder: self.t('handoff_phone_placeholder')
            },
            actions: [
              {
                label: this.t('talk_to_designer'),
                primary: true,
                onClick: function (phone) { self.handleHandoff(phone, eventDate); }
              },
              {
                label: this.t('rush_pick_date'),
                onClick: function () {
                  document.dispatchEvent(
                    new CustomEvent('studio-designer:focus-event-date', { bubbles: true })
                  );
                }
              }
            ]
          });
        }

        /** App demands login (401) — replace the frame with a sign-in CTA. */
        renderLoginGate() {
          this.session = null;
          var frame = this.querySelector('[data-studio-chat-frame]') || this;
          frame.innerHTML = '';
          var wrap = document.createElement('div');
          wrap.className = 'studio-chat studio-chat--login';
          var copy = document.createElement('p');
          copy.className = 'studio-chat__login-copy';
          copy.textContent = this.t('login_title');
          var link = document.createElement('a');
          link.className = 'button button--primary studio-chat__login-button';
          link.href = this.loginUrl || '/account/login';
          link.textContent = this.t('login_button');
          wrap.appendChild(copy);
          wrap.appendChild(link);
          frame.appendChild(wrap);
          this.emitStatus();
        }

        /** Feature switched off (403) — hide the widget behind a notice. */
        renderDisabled() {
          this.session = null;
          var frame = this.querySelector('[data-studio-chat-frame]') || this;
          frame.innerHTML = '';
          var wrap = document.createElement('div');
          wrap.className = 'studio-chat studio-chat--disabled';
          var copy = document.createElement('p');
          copy.className = 'studio-chat__disabled-copy';
          copy.textContent = this.t('feature_disabled');
          wrap.appendChild(copy);
          frame.appendChild(wrap);
          this.emitStatus();
        }

        renderAll() {
          this.renderMessages();
          this.renderActions();
          this.renderSlots();
          this.syncComposerLock();
        }

        /**
         * The design picker: WHICH approved design this order prints.
         *
         * One tray, one design (`slotsForSelection`), so this is a choice
         * between alternatives, not a set of slots to fill: the customer asks
         * the bot for as many designs as they like and taps the one they want
         * printed. Tapping never removes work — every design they approved
         * stays here, and picking a different one is a single tap back.
         *
         * It is a chooser, NOT a to-do list. USER, 2026-09-06: an empty "add
         * a design" tile on a conversation that has produced nothing yet only
         * confuses — the bot below is already asking for the design. So the
         * picker appears only once there is something to pick, and carries no
         * empty tile: another design is asked for in the conversation.
         */
        renderSlots() {
          var host = this.els && this.els.slots;
          if (!host) return;
          var designs = (this.session && this.session.designs) || null;
          /* Shown on every product that sells BY THE PIECE, including a 1 kg
             tray with its single slot. Products that do not (a backdrop is one
             board) have no kilos to divide and get nothing. */
          if (!designs || this.piecesPerDesign() <= 0) {
            host.hidden = true;
            host.innerHTML = '';
            return;
          }
          /* Nothing approved yet: no picker at all, hint included. Until the
             bot has made something, there is nothing here to choose between
             and the row is pure noise above the conversation. */
          if (!(designs.approved || []).length) {
            host.hidden = true;
            host.innerHTML = '';
            return;
          }
          var cap = this.slotsForSelection();
          /* EVERY design the customer has approved, however many the size can
             carry. USER, 2026-09-01: "all the designs must be shown and the
             allowed select count should depend on the selected option." One
             design per kilo caps how many go ON the order, not how many exist
             — dropping to 1 kg after making three must not look like the shop
             lost two of them. */
          var approved = (designs.approved || []).slice();
          var selected = this.selectedSlots();
          var self = this;
          var row = document.createElement('div');
          row.className = 'studio-chat__slots-row';

          approved.forEach(function (entry) {
            var slot = Number(entry.slot);
            var on = selected.indexOf(slot) !== -1;
            var button = document.createElement('button');
            button.type = 'button';
            button.className = 'studio-chat__slot' + (on ? ' is-selected' : '');
            button.setAttribute('data-studio-slot', String(slot));
            button.setAttribute('aria-pressed', on ? 'true' : 'false');

            var thumb = document.createElement('img');
            thumb.className = 'studio-chat__slot-thumb';
            thumb.src = self.assetUrl(entry.assetId || entry.asset_id);
            thumb.alt = '';
            thumb.loading = 'lazy';
            button.appendChild(thumb);

            var label = document.createElement('span');
            label.className = 'studio-chat__slot-label';
            label.textContent = self
              .t(on ? 'slot_selected' : 'slot_unselected')
              .replace('{{ number }}', String(slot));
            button.appendChild(label);
            /* The thumbnail carries no alt text on purpose — a screen reader
               cannot describe generated artwork, and `aria-pressed` above
               already says selected or not. */
            button.setAttribute(
              'aria-label',
              self.t('slot_toggle_a11y').replace('{{ number }}', String(slot))
            );
            button.addEventListener('click', function (event) {
              self.toggleDesign(Number(event.currentTarget.getAttribute('data-studio-slot')));
            });
            row.appendChild(button);
          });

          var hint = document.createElement('p');
          hint.className = 'studio-chat__slots-hint';
          /* One sentence, because there is one situation: several designs to
             choose from, one of them going on this order. Silent while there
             is only one design — a chooser with nothing to choose explains
             itself. */
          hint.textContent = approved.length > 1 ? this.t('slots_hint_pick') : '';
          hint.hidden = !hint.textContent;
          host.innerHTML = '';
          host.appendChild(row);
          host.appendChild(hint);
          host.hidden = false;
        }

        /** The slots on the order — the server's answer, capped locally. */
        selectedSlots() {
          var designs = (this.session && this.session.designs) || null;
          var chosen = (designs && (designs.selected || [])).map(Number);
          var approved = ((designs && designs.approved) || []).map(function (entry) {
            return Number(entry.slot);
          });
          if (!chosen.length) chosen = approved;
          /* Capped against the SELECTED variant, not the size the server last
             heard about: the shopper may have just moved the picker, and a
             selection of three highlighted on a 1 kg order is the picker
             promising what the tray cannot hold. */
          return chosen.slice(0, this.slotsForSelection());
        }

        /**
         * Put a design on the order — a switch, never a toggle off.
         *
         * One design goes on the tray, so tapping another design REPLACES the
         * one selected rather than adding to it, and tapping the selected one
         * does nothing: an order carrying no design at all is not a state the
         * customer can want, and letting one tap produce it turns the buy
         * button off with no explanation of what undid it.
         */
        toggleDesign(slot) {
          if (this.pending || this.locked) return;
          var chosen = this.selectedSlots().slice();
          if (chosen.length === 1 && chosen[0] === slot) return;
          chosen = [slot];
          /* Painted immediately from the local answer, then confirmed by the
             server's. A picker that waits a round trip to show a tap feels
             broken on a phone. */
          if (this.session && this.session.designs) {
            this.session.designs.selected = chosen;
          }
          this.renderSlots();
          this.saveSelection(chosen);
        }

        /** Persist the selection; the ORDER reads it, not the picker. */
        saveSelection(slots) {
          var self = this;
          this.postAction('/designs', { slots: slots })
            .then(function (data) {
              if (data && data.session) self.session = data.session;
              self.renderSlots();
              self.emitStatus();
            })
            .catch(function () {
              /* The server is the authority and the next poll corrects us.
                 Shouting about a failed preference save would be louder than
                 the problem — and `postAction` already rejects silently for
                 the common case, a tap that raced an in-flight turn. */
            });
        }

        /** Ask the bot to start, or revisit, one design. */
        pickSlot(slot, done) {
          if (this.pending || this.locked) return;
          var text = this.t(done ? 'slot_change_prompt' : 'slot_add_prompt').replace(
            '{{ number }}',
            String(slot)
          );
          this.sendMessage({ text: text });
        }

        /**
         * Keyed reconciliation instead of a wholesale rebuild: every row
         * (bubble / asset card / typing indicator) carries a stable key and a
         * content fingerprint. Rows whose fingerprint is unchanged keep their
         * DOM nodes untouched, so the 2 s poll tick mutates nothing when the
         * snapshot hasn't changed — no image re-fetches, no scroll yank, and
         * the `aria-live` log doesn't re-announce history to screen readers.
         * Autoscroll only happens when the user is already pinned near the
         * bottom (or the list is empty).
         */
        renderMessages() {
          var self = this;
          var list = this.els.messages;
          var rows = [];

          if (!this.messages.length) {
            rows.push({
              key: 'greeting',
              hash: 'greeting',
              build: function () { return self.buildBubble('bot', self.t('greeting'), null); }
            });
          }

          this.messages.forEach(function (message, index) {
            var text = message.contentType === 'audio' && !message.text ? self.t('voice_note') : message.text;
            var imageAssetId = message.contentType === 'image' ? message.assetId : null;
            var role = message.role === 'customer' ? 'customer' : 'bot';
            rows.push({
              key: 'm' + index,
              hash: role + '|' + (text || '') + '|' + (imageAssetId || ''),
              /* An optimistic bubble (typed a second ago, not yet acknowledged)
                 has no timestamp — it is the newest thing there is, so it sorts
                 last against every design. */
              at: message.createdAt || LAST,
              build: function () { return self.buildBubble(role, text, imageAssetId); }
            });
          });

          this.mergeDesignRows(rows);
          this.collectQuickReplyRow(rows);
          this.collectTypingRow(rows);

          var nearBottom =
            !list.childElementCount ||
            list.scrollHeight - list.scrollTop - list.clientHeight < 48;

          var existing = {};
          Array.prototype.slice.call(list.children).forEach(function (el) {
            existing[el.dataset.studioKey] = el;
          });

          var cursor = null;
          rows.forEach(function (row) {
            var el = existing[row.key];
            if (el && el.dataset.studioHash !== row.hash) {
              var fresh = row.build();
              fresh.dataset.studioKey = row.key;
              fresh.dataset.studioHash = row.hash;
              el.replaceWith(fresh);
              el = fresh;
            } else if (!el) {
              el = row.build();
              el.dataset.studioKey = row.key;
              el.dataset.studioHash = row.hash;
              list.insertBefore(el, cursor ? cursor.nextSibling : list.firstChild);
            }
            /* A kept node still has to be in the right PLACE. Rows are merged
               by timestamp now, so a design card can arrive between two
               messages it used to sit below, and reusing the node without
               moving it left the DOM in the old order until a reload.
               `insertBefore` on a node already in position is a no-op, so this
               costs nothing on the ordinary tick. */
            var want = cursor ? cursor.nextSibling : list.firstChild;
            if (el !== want) list.insertBefore(el, want);
            delete existing[row.key];
            cursor = el;
          });
          Object.keys(existing).forEach(function (key) {
            existing[key].remove();
          });

          if (nearBottom) list.scrollTop = list.scrollHeight;
        }

        buildBubble(role, text, imageAssetId) {
          var bubble = document.createElement('div');
          bubble.className = 'studio-chat__bubble studio-chat__bubble--' + role;
          if (role === 'bot') {
            var name = document.createElement('span');
            name.className = 'studio-chat__bubble-author';
            name.textContent = this.t('bot_name');
            bubble.appendChild(name);
          }
          if (imageAssetId) {
            var img = document.createElement('img');
            img.className = 'studio-chat__bubble-image';
            img.loading = 'lazy';
            img.alt = '';
            img.src = this.assetUrl(imageAssetId);
            bubble.appendChild(img);
          }
          if (text) {
            var p = document.createElement('p');
            p.className = 'studio-chat__bubble-text';
            p.textContent = text;
            bubble.appendChild(p);
          }
          return bubble;
        }

        /** Generated unit / composed-layout previews with approve rows. */
        /**
         * The image to SHOW for a design — its composed product mockup where
         * one exists, otherwise the artwork itself.
         *
         * Paired by `sourceAssetId`, not by "the newest preview". The newest
         * one belongs to the newest design, so asking for a PAST design gave
         * back either another design's mockup or, once the newest artwork was
         * still generating, the raw flat artwork. Either way the customer
         * scrolling back to the design they liked was shown something that
         * did not look like it — same design, different picture — and read
         * that as their design having been changed. "The user does not know
         * how to go back and get to her other design, which was the correct
         * shade of green that she wanted" (reported 2026-09-01).
         *
         * Two-phase products always show the raw design (their on-product
         * moment is the layout step), and print-sheet products keep the
         * printed-unit CSS framing.
         */
        displayAssetFor(unit) {
          if (!unit || unit.status !== 'READY' || this.hasLayoutPhase()) return unit;
          var product = this.activeProduct();
          if (!product || product.outputMode === 'PRINT_SHEET') return unit;
          var newest = null;
          (this.assets || []).forEach(function (asset) {
            if (asset.kind !== 'UNIT_PREVIEW' || asset.status !== 'READY') return;
            if (asset.sourceAssetId !== unit.id) return;
            /* Newest wins: a re-approval composes a fresh mockup for the same
               design and the stale one must not outrank it. */
            if (!newest || asset.createdAt >= newest.createdAt) newest = asset;
          });
          return newest || unit;
        }

        /**
         * Put every design card where it HAPPENED, between the messages that
         * surround it, instead of below the whole transcript.
         *
         * The cards used to be appended after the last message, so asking for
         * a second design left the first one sitting under the bot's new
         * question — reported as reading like the answer to it: "the previous
         * design always shows under the text... I always think this is the new
         * design" (USER, 2026-09-06). The customer asked for the transcript to
         * keep its own order, not for the old design to be hidden.
         *
         * Merged by `createdAt`, which both messages and assets carry, so the
         * order is the server's rather than this render's. Anything without a
         * timestamp (an optimistic bubble) is newest by definition and sorts
         * last.
         */
        mergeDesignRows(rows) {
          var designs = [];
          this.collectDesignRows(designs);
          designs.sort(function (a, b) {
            return String(a.at || LAST) < String(b.at || LAST) ? -1 : 1;
          });
          var merged = [];
          var next = 0;
          rows.forEach(function (row) {
            var at = String(row.at || LAST);
            while (next < designs.length && String(designs[next].at || LAST) <= at) {
              merged.push(designs[next++]);
            }
            merged.push(row);
          });
          while (next < designs.length) merged.push(designs[next++]);
          rows.length = 0;
          Array.prototype.push.apply(rows, merged);
        }

        collectDesignRows(rows) {
          var self = this;
          var status = this.session && this.session.status;
          var twoPhase = this.hasLayoutPhase();
          var units = (this.assets || []).filter(function (asset) {
            return asset.kind === 'UNIT_DESIGN';
          });
          var unit = units.length ? units[units.length - 1] : null;
          var layout = this.latestAssetOfKind('LAYOUT_PREVIEW');
          /* Each design is shown the way the customer sees it on the product
             — see `displayAssetFor`. */
          var product = this.activeProduct();
          var unitDisplay = this.displayAssetFor(unit);

          /* Fingerprint covers everything that changes the card's rendering:
             asset id + status + error, the session status (button visibility),
             the flow shape and the poll-timeout flag. */
          function cardHash(asset) {
            return [
              asset.id,
              asset.status,
              asset.error || '',
              asset.approvedAt || '',
              status || '',
              twoPhase ? '2p' : '1p',
              asset.id === self.pollTimedOutAssetId ? 'timeout' : ''
            ].join('|');
          }

          /* Every earlier generation stays in the chat so the customer can
             compare options and go back to one they preferred — the snapshot
             always carried them, only the render threw them away. Display-only:
             approving or retrying an old design is meaningless, so those rows
             belong to the newest card alone. READY-only for the same reason —
             a superseded failure is noise, and its retry would act on the
             CURRENT generation, not the one the card shows. Their hash is
             content-free (a READY asset never changes again), so the 2 s poll
             leaves these nodes and their images completely untouched. */
          units.slice(0, -1).forEach(function (past) {
            if (past.status !== 'READY') return;
            /* Shown the way the customer SAW it — its own mockup, not the raw
               artwork. This row exists so they can go back to a design they
               preferred, and rendering the flat artwork instead made the one
               they wanted look like a different design. */
            var pastDisplay = self.displayAssetFor(past);
            rows.push({
              key: 'asset-' + past.id,
              at: past.createdAt || '',
              /* Keyed on what is actually rendered: the mockup is composed
                 asynchronously, so this row starts on the artwork and must
                 re-render once its mockup lands. A `past.id`-only hash left
                 it on the artwork forever. */
              hash: 'history|' + past.id + '|' + pastDisplay.id,
              build: function () {
                return self.buildAssetCard(pastDisplay, null, { history: true });
              }
            });
          });

          if (unit) {
            rows.push({
              key: 'asset-' + unit.id,
              at: unit.createdAt || '',
              hash: cardHash(unitDisplay) + '|' + unit.status + '|' + (unit.approvedAt || ''),
              build: function () {
                return self.buildAssetCard(unitDisplay, function (card) {
                  /* An ALREADY-APPROVED design gets a marker, never the button
                     again. On a multi-design tray the chat stays open after
                     the first approval, so the newest card can be a design the
                     customer already signed off while the bot is asking about
                     the next one — and re-offering "Approve design" there
                     reads as being asked to approve something they never
                     described (reported 2026-08-10). */
                  /* ABOVE the approved-design early return, so "how big is
                     it?" is still answerable once the design is signed off —
                     that is exactly when someone measures their wall. A quiet
                     link rather than a button: the row below deliberately asks
                     one question. */
                  if (unit.status === 'READY' && self.sizeGuideUrl()) {
                    var guide = document.createElement('button');
                    guide.type = 'button';
                    guide.className = 'studio-chat__size-guide';
                    guide.textContent = self.t('view_size_guide');
                    guide.addEventListener('click', function () { self.openSizeGuide(); });
                    card.appendChild(guide);
                  }
                  if (unit.approvedAt) {
                    var done = document.createElement('p');
                    done.className = 'studio-chat__asset-copy studio-chat__asset-copy--approved';
                    done.textContent = self.t('design_locked_in');
                    card.appendChild(done);
                    return;
                  }
                  if (unit.status === 'READY' && status === 'DRAFT') {
                    /* ONE button. "Talk to a designer" was a second, equally
                       sized button here while the same action already sits in
                       the header of every screen — two identical escape
                       hatches stacked under the artwork, which is most of what
                       made this card feel cluttered on a phone. The header
                       link is always reachable, so the card asks the one
                       question it exists to ask. */
                    card.appendChild(
                      self.buildButtonRow([
                        { label: self.t('approve_design'), primary: true, onClick: function () { self.handleApproveUnit(); } }
                      ])
                    );
                  }
                });
              }
            });
          }

          /* NO tray card for single-approval products. One was added on
             2026-08-10 and rejected on sight: the chocolate flow deliberately
             has no tray step, and a second image with no button under it reads
             as another thing the customer has to do. The whole-tray
             reassurance lives in the close-up instead, whose surrounding
             pieces now carry the design — including both designs of a split.

             The composed-layout phase only exists for two-phase products
             (config `hasLayoutPhase`); single-approval products go straight to
             the approved state after the unit approval. */
          if (twoPhase && layout && (status === 'UNIT_APPROVED' || status === 'LAYOUT_APPROVED' || status === 'SENT_TO_DWS')) {
            rows.push({
              key: 'asset-' + layout.id,
              at: layout.createdAt || '',
              hash: cardHash(layout),
              build: function () {
                return self.buildAssetCard(layout, function (card) {
                  if (layout.status === 'READY') {
                    var copy = document.createElement('p');
                    copy.className = 'studio-chat__asset-copy';
                    copy.textContent = self.t('layout_copy');
                    card.appendChild(copy);
                  }
                  if (layout.status === 'READY' && status === 'UNIT_APPROVED') {
                    card.appendChild(
                      self.buildButtonRow([
                        { label: self.t('approve_layout'), primary: true, onClick: function () { self.handleApproveLayout(); } },
                        { label: self.t('talk_to_designer'), onClick: function () { self.confirmHandoff(); } }
                      ])
                    );
                  }
                });
              }
            });
          }
        }

        buildAssetCard(asset, decorate, options) {
          var self = this;
          var product = this.activeProduct();
          var history = !!(options && options.history);
          /* The printed-unit framing (white unit inset in a product-coloured
             square) only makes sense for print-sheet products; mockup-only
             products show the raw generated design. */
          var isUnit =
            asset.kind === 'UNIT_DESIGN' &&
            (!product || product.outputMode === 'PRINT_SHEET');
          /* A generation the poll valve gave up on renders as FAILED — a
             stuck "generating" card with no way out is a dead end. */
          var timedOut = asset.status === 'GENERATING' && asset.id === this.pollTimedOutAssetId;
          var card = document.createElement('div');
          card.className =
            'studio-chat__asset studio-chat__asset--' + (timedOut ? 'failed' : asset.status.toLowerCase()) +
            (isUnit ? ' studio-chat__asset--unit' : ' studio-chat__asset--layout') +
            (history ? ' studio-chat__asset--history' : '');

          if (asset.status === 'GENERATING' && !timedOut) {
            var generating = document.createElement('p');
            generating.className = 'studio-chat__generating';
            generating.textContent = this.t('generating');
            card.appendChild(generating);
          } else if (asset.status === 'FAILED' || timedOut) {
            var error = document.createElement('p');
            error.className = 'studio-chat__asset-error';
            error.textContent = timedOut
              ? this.t('error_generation_timeout')
              : asset.error || this.t('error_generation_failed');
            card.appendChild(error);
            card.appendChild(
              this.buildButtonRow([
                { label: this.t('retry'), onClick: function () { self.handleRetryGeneration(); } }
              ])
            );
          } else {
            var img = document.createElement('img');
            img.className = 'studio-chat__asset-image';
            img.loading = 'lazy';
            img.alt = '';
            img.src = this.assetUrl(asset.id);
            if (isUnit) {
              /* Present the generated unit as a real printed piece inset in a
                 product-coloured square (CSS framing only — the artwork itself
                 is untouched). Layout previews stay unframed: the server
                 already composited them onto the real product photo. */
              var frame = document.createElement('div');
              frame.className = 'studio-unit-frame';
              img.classList.add('studio-unit-frame__img');
              frame.appendChild(img);
              card.appendChild(frame);
            } else {
              card.appendChild(img);
            }
          }

          if (decorate) decorate(card);
          return card;
        }

        buildButtonRow(buttons) {
          var row = document.createElement('div');
          row.className = 'studio-chat__button-row';
          buttons.forEach(function (config) {
            var button = document.createElement('button');
            button.type = 'button';
            button.className = 'button studio-chat__action-button' + (config.primary ? ' button--primary' : ' button--secondary');
            button.textContent = config.label;
            button.addEventListener('click', config.onClick);
            row.appendChild(button);
          });
          return row;
        }

        /**
         * Tappable openers, so the chat never presents a blank box and one
         * open question. Testers didn't know what to type, and several never
         * recognised the bot as the design step at all ("some might think it's
         * customer support"). The chips also carry the phrasing the bot handles
         * best — "no text, just the photo" is the exact instruction it used to
         * ignore.
         *
         * Static and contextual: no model round-trip, no API change. Each chip
         * either sends its own label as a message (that is what a quick reply
         * IS — what you see is what you say) or runs a local action.
         *
         * Deliberately absent: a "print as-is" chip. The capability does not
         * exist — watercolour is hard-coded in both prompts — and offering it
         * is exactly the false promise testers already hit. It arrives with
         * T14, if D2 says it should.
         */
        quickReplies() {
          var self = this;
          var status = this.session && this.session.status;
          /* Only while the customer is still designing, and only when the
             composer is live — a chip that posts into a locked session, or
             races an in-flight turn, is worse than no chip. */
          if (status !== 'DRAFT' || this.busy || this.pollingAssetId || this.activeUpload) return [];

          /* A design the customer has already settled answers its own chips.
             On a split tray the session is still DRAFT right after the first
             approval, so both the bot's own chips ("Approve", "Change design")
             and the static refinements below survived the approval that
             answered them — leaving an "Approve" chip under an "Approved ✓"
             card (reported 2026-08-10).

             But going silent here was wrong too: DRAFT with an approved newest
             design is EXACTLY mid-split (a finished tray is UNIT_APPROVED), and
             it is the moment the bot asks "now let's design number 2 of 2 —
             what would you like on it?". That question arrived with no tappable
             answers at all, because the backend writes it deterministically and
             sends no suggestions with it. So: drop the settled design's own
             chips, and fall through to the OPENING set — a new design is being
             started, not refined. */
          var latestUnit = this.latestAssetOfKind('UNIT_DESIGN');
          var settled = !!(latestUnit && latestUnit.status === 'READY' && latestUnit.approvedAt);

          /* The bot's own answers to the question it just asked win over any
             static set: it knows what it asked, we only know the state. "It
             would be nice to give the person options buttons to click rather
             than they type everything out" — this is that, and it works for a
             question we never anticipated. */
          var lastBot = null;
          (this.messages || []).forEach(function (message) {
            if (message.role === 'bot') lastBot = message;
          });
          var proposed = (settled ? [] : (lastBot && lastBot.suggestions)) || [];
          if (proposed.length) {
            var escalation = this.t('talk_to_designer');
            return proposed.map(function (label, index) {
              /* "Talk to a designer" is an ACTION, not an answer — see
                 HANDOFF_LABELS. Everything else is sent as the customer's
                 own words, exactly as the bot proposed them. */
              if (isHandoffLabel(label, escalation)) {
                return {
                  key: 'ai-' + index,
                  label: label,
                  run: function () { self.confirmHandoff(); }
                };
              }
              return { key: 'ai-' + index, label: label, send: label };
            });
          }

          /* Everything this chat has ALREADY offered. A static chip that
             repeats an option the customer has just turned down is the loop
             QA reported on 2026-09-01: the bot answers "Something else" with
             an open question — correctly, and with no suggestions of its own —
             and the fallback set underneath re-offers Birthday and Graduation,
             the two they had just refused. The bot is blind to these chips
             (they are ours, not its), so filtering them is this side's job.

             Actions are exempt: "Upload a photo" and "Just generate it now"
             are ways OUT of the question, not answers to it, and stay useful
             however many times they are shown. */
          var alreadyOffered = {};
          (this.messages || []).forEach(function (message) {
            if (message.role !== 'bot') return;
            (message.suggestions || []).forEach(function (label) {
              alreadyOffered[String(label).trim().toLowerCase()] = true;
            });
          });
          function isFresh(chip) {
            if (!chip || chip.run) return true;
            return !alreadyOffered[String(chip.label).trim().toLowerCase()];
          }

          var upload = {
            key: 'photo',
            label: this.t('chip_upload_photo'),
            run: function () { self.els.file.click(); }
          };
          function say(key, labelKey) {
            return { key: key, label: self.t(labelKey), send: self.t(labelKey) };
          }

          /* READY, not merely present: a FAILED generation leaves an asset
             behind, and offering "change the colours" for a design that does
             not exist sends the customer in a circle. Its card carries Retry;
             the chips fall through to whatever they can still act on.

             `!settled`: refinements are for the design in hand. Once it is
             approved the next message starts the NEXT design of the split, and
             "change the colours" would be aimed at nothing. */
          if (!settled && latestUnit && latestUnit.status === 'READY') {
            /* A design is on screen. Approve / Start over are already buttons
               on the card, so these are only the refinements. */
            return [
              say('colours', 'chip_change_colours'),
              say('bigger', 'chip_bigger_text'),
              say('name', 'chip_add_name')
            ];
          }

          /* "The generate button should always be there if the user wants to
             see a design immediately" — the bot asks about whatever side is
             missing (T10), which is right for someone still deciding and in
             the way of someone who already knows. This skips the question
             without removing it. Offered only once the customer has actually
             said something, since there is nothing to generate from before
             that. */
          var saidSomething = (this.messages || []).some(function (message) {
            return message.role === 'customer';
          });
          var generateNow = saidSomething ? [say('generate', 'chip_generate_now')] : [];

          if (settled) {
            /* Mid-split: the design on screen is locked in and the bot has just
               asked what goes on the next one. The photo/text answers below are
               wrong here — the photo already in the thread belongs to the design
               that is finished, and offering "no text, just the photo" reads as
               being asked about it again. Not the occasion set either: the
               occasion was established for design 1 and is not being re-chosen. */
            return [upload, say('text-only', 'chip_text_only')];
          }

          var hasPhoto = (this.messages || []).some(function (message) {
            return message.contentType === 'image';
          });
          if (hasPhoto || this.pendingAttachment) {
            /* Photo in hand — the bot's next question is about text (T10), so
               answer it in one tap, either way. */
            return [say('no-text', 'chip_no_text'), say('name', 'chip_add_name')].concat(
              generateNow
            );
          }

          /* THE BOT ASKED SOMETHING AND DELIBERATELY OFFERED NO ANSWERS.
             It is told to do exactly that when the customer taps "Something
             else": none of these fit, so the only useful next move is for
             them to say it in their own words. The occasion set below would
             then answer a question nobody asked — QA tapped "Something else"
             on a list of occasions and got another list of occasions under a
             reply that said "tell me in your own words" (2026-09-01). Word-
             for-word filtering does not catch it, because the second list is
             a DIFFERENT set of occasions.

             Scoped tightly: only when a bot turn has actually spoken and
             chose to offer nothing. The greeting (no bot message yet) keeps
             its openers, and every earlier branch — the refinements under a
             finished design, the mid-split pair, the photo answers — has
             already returned by here. */
          if (lastBot && lastBot.text && !(lastBot.suggestions || []).length) {
            return generateNow;
          }

          /* The opening set answers the greeting's actual question — "what kind
             of special design or message?" — with OCCASIONS.
             It used to offer "Upload a photo / Just text, no picture / Surprise
             me". Only the first earned its place: "just text" still leaves the
             person with a blank page to write, and "surprise me" reads as
             someone messing around rather than a real intent. An occasion is
             one tap that tells the bot something it can design from, and it
             sets up the follow-up the prompt now insists on — whose it is and
             what it should say. */
          return [
            upload,
            say('birthday', 'chip_occasion_birthday'),
            say('congrats', 'chip_occasion_congrats'),
            say('baby', 'chip_occasion_baby'),
            say('graduation', 'chip_occasion_graduation')
          ]
            .filter(isFresh)
            .concat(generateNow);
        }

        collectQuickReplyRow(rows) {
          var self = this;
          var chips = this.quickReplies();
          if (!chips.length) return;
          rows.push({
            key: 'quick-replies',
            /* The LABELS, not just the keys. Model-proposed chips are keyed by
               position ('ai-0', 'ai-1', …), so two consecutive bot questions
               offering the same NUMBER of answers hashed identically — the
               renderer saw no change, left the row alone, and the customer was
               shown the previous question's answers. Worse than stale text:
               `chip.send` is the old label too, so tapping "Birthday" posted
               "Birthday" as the answer to a question about colours. */
            hash:
              'chips|' +
              chips
                .map(function (chip) { return chip.key + '\u001f' + chip.label; })
                .join('\u001e'),
            build: function () {
              var row = document.createElement('div');
              row.className = 'studio-chat__chips';
              chips.forEach(function (chip) {
                var button = document.createElement('button');
                button.type = 'button';
                button.className = 'studio-chat__chip';
                button.textContent = chip.label;
                button.addEventListener('click', function () {
                  if (chip.run) chip.run();
                  else self.sendMessage({ text: chip.send });
                });
                row.appendChild(button);
              });
              return row;
            }
          });
        }

        collectTypingRow(rows) {
          if (!this.busy && !this.pollingAssetId) return;
          var text = this.pollingAssetId ? this.t('generating') : this.t('typing');
          rows.push({
            key: 'typing',
            hash: 'typing|' + text,
            build: function () {
              var typing = document.createElement('div');
              typing.className = 'studio-chat__bubble studio-chat__bubble--bot studio-chat__bubble--typing';
              typing.textContent = text;
              return typing;
            }
          });
        }

        renderActions() {
          /* Needed by the button handlers below. #61 dropped this line together
             with the start-over button that used it, so the merge deleted it
             while keeping this branch's Add-to-bag handler — and `self` would
             have silently resolved to window.self, throwing only on click. */
          var self = this;
          var actions = this.els.actions;
          actions.innerHTML = '';
          var status = this.session && this.session.status;

          if ((status && this.isFinalApproved(status)) || status === 'SENT_TO_DWS') {
            var approved = document.createElement('div');
            approved.className = 'studio-chat__state studio-chat__state--approved';
            var approvedTitle = document.createElement('strong');
            approvedTitle.textContent = this.t('approved_title');
            var approvedCopy = document.createElement('p');
            /* The box facts, not "you can now add your approved design to the
               bag" — the title above already says the design is approved and
               the button below already says Add to bag, so that sentence was
               telling the customer nothing twice while the one useful thing
               sat under the fold. "Where's the information about the box?...
               replace it with the disclaimer." Falls back to the old line if
               the config never loaded, so the card is never left empty. */
            var approvedProduct = this.activeProduct();
            var approvedFacts = productFacts(
              function (key) { return self.t(key); },
              approvedProduct ? this.pieceCountForSelection() : null,
              (approvedProduct || {}).productMm || null,
              this.choiceNoteForSelection(),
              productIsPieceCounted(approvedProduct),
              this.packagingNoteForLocale()
            );
            approvedCopy.textContent = approvedFacts.length
              ? approvedFacts.join(' ')
              : this.t('approved_copy');
            approved.appendChild(approvedTitle);
            /* WHAT THEY ARE ACTUALLY GETTING, at the one moment it matters.
               A print-sheet product shows its designs as flat artwork in a CSS
               frame (see `displayAssetFor`), so until now a chocolate customer
               never saw the tray at all — the backend assumed "the close-up
               already shows the whole tray wearing it", but the close-up is
               the one thing that flow does not render. One image on the
               approved card, not a second approval step: the tray step itself
               was rejected on sight (2026-08-10) as "another thing to do". */
            var trayUrl = this.approvedLayoutUrl();
            if (trayUrl) {
              var tray = document.createElement('img');
              tray.className = 'studio-chat__state-tray';
              tray.src = trayUrl;
              tray.alt = this.t('approved_tray_alt');
              tray.setAttribute('loading', 'lazy');
              approved.appendChild(tray);
            }
            approved.appendChild(approvedCopy);
            /* The moment the design is approved, the ONLY thing the customer
               wants is to buy it — this card used to offer "Start over"
               instead, and the real buy button sat below the fold where
               shoppers (and the CTO) failed to find it. SENT_TO_DWS is past
               that point (the order is placed and the request is with the
               designers), so it gets the copy alone.

               No start-over button here either way: the composer footer
               already carries a persistent "Start over", and two of them side
               by side read as two different actions. (#61 removed it from this
               card at the same time this branch did — same conclusion, reached
               twice; this keeps both the removal and the buy button.) */
            if (status && this.isFinalApproved(status)) {
              approved.appendChild(
                this.buildButtonRow([
                  { label: this.t('add_to_bag'), primary: true, onClick: function () { self.requestAddToBag(); } }
                ])
              );
            }
            actions.appendChild(approved);
          } else if (status === 'HANDED_OFF') {
            var handed = document.createElement('div');
            handed.className = 'studio-chat__state studio-chat__state--handoff';
            var handedCopy = document.createElement('p');
            handedCopy.textContent = this.t('handed_off');
            handed.appendChild(handedCopy);
            /* On resume there is no lastWhatsappUrl — that only survives the
               tab that performed the handoff. The snapshot re-echoes it for a
               handed-off session precisely so a returning customer still has a
               way through; without the fallback the copy above promises
               WhatsApp and offers no button. */
            var whatsappUrl = this.lastWhatsappUrl || (this.session && this.session.whatsappUrl);
            if (whatsappUrl) {
              /* Primary path, not a fallback: window.open() runs after an
                 async POST (and now after a modal), so browsers block it
                 often enough that the popup can never be the only way out. */
              var link = document.createElement('a');
              link.className = 'button button--primary studio-chat__action-button';
              link.href = whatsappUrl;
              link.target = '_blank';
              link.rel = 'noopener';
              link.textContent = this.t('open_whatsapp');
              handed.appendChild(link);
            }
            /* No Start over button HERE. The handoff parks this session for
               good — composer locked, status server-side, refresh won't clear
               it — so the customer does need a way out, and testers said the
               footer link was "too small to notice". But Aziz was explicit:
               "we don't need to have a start over everywhere... that should be
               the only one to start over", and his fix for the visibility was
               to promote the footer control rather than add a second one:
               "the start over can be more visible... we can put a button on
               the bottom right".
               That is what shipped — the footer restart is a button now — so
               a second control in this state would be the duplication he asked
               us not to build. The copy above points at it. */
            actions.appendChild(handed);
          }
        }

        syncComposerLock() {
          var status = this.session && this.session.status;
          /* Approval is NOT the end of the conversation. It used to lock the
             composer here, which is what made "make me a second design"
             impossible to ask for: the customer designs one tile, approves it,
             and the only way to add another was to know to say so BEFORE
             starting (reported 2026-08-10). Approving means "this design is
             settled", not "I am finished shopping" — the backend accepts a
             message in UNIT_APPROVED and reopens the session, and Add to bag
             simply greys out again until the new design is approved too.

             Only genuinely terminal states lock: the session has left the
             customer's hands (handed to a designer, submitted) or expired. */
          var locked =
            this.busy ||
            !!this.pollingAssetId ||
            status === 'HANDED_OFF' ||
            status === 'SENT_TO_DWS' ||
            status === 'EXPIRED';
          this.els.input.disabled = locked;
          this.els.send.disabled = locked;
          this.els.attach.disabled = locked;
          this.els.mic.disabled = locked;
          this.els.composer.classList.toggle('studio-chat__composer--locked', locked);
          /* "Talk to a designer" stays available while a photo is uploading —
             it is the escape hatch, and a multi-minute upload must never be
             able to take it away. */
          this.els.handoff.disabled = (this.busy && !this.activeUpload) || status === 'HANDED_OFF';
        }

        showNotice(text, tone, emphasizeHandoff) {
          this.els.notice.textContent = text || '';
          this.els.notice.hidden = !text;
          this.els.notice.className = 'studio-chat__notice' + (tone === 'error' ? ' studio-chat__notice--error' : '');
          this.els.handoff.classList.toggle('studio-chat__handoff--emphasized', !!emphasizeHandoff);
        }

        /* ---------------- message send ---------------- */

        /** Remember a chosen file (and forget any upload from a previous pick). */
        setPendingAttachment(file, assetId, assetSessionId) {
          this.pendingAttachment = file;
          this.pendingAttachmentAssetId = assetId || null;
          this.pendingAttachmentSessionId = assetSessionId || null;
          this.els.chipLabel.textContent = this.t('image_attached');
          this.els.chip.hidden = false;
        }

        clearPendingAttachment() {
          this.pendingAttachment = null;
          this.pendingAttachmentAssetId = null;
          this.pendingAttachmentSessionId = null;
          this.els.file.value = '';
          this.els.chip.hidden = true;
        }

        handleSend() {
          /* Guard BEFORE clearing state: sendMessage() also returns early when
             busy, and the direct-upload path stretches that window from ~1s to
             several minutes — clearing first would silently discard the whole
             payload. */
          if (this.busy) return;
          var text = this.els.input.value.trim();
          if (!text && !this.pendingAttachment && !this.pendingAudio) return;
          var payload = { text: text };
          if (this.pendingAttachment) {
            payload.image = this.pendingAttachment;
            /* A retry after a failed send re-references the already-uploaded
               asset instead of re-uploading the (possibly 50 MB) file — but only
               if it belongs to the CURRENT session. After Start Over the id
               points at the previous session's asset, which the backend rejects
               forever; dropping it here re-uploads instead of dead-ending. */
            var currentSessionId = this.session && this.session.id;
            if (this.pendingAttachmentAssetId && this.pendingAttachmentSessionId === currentSessionId) {
              payload.imageAssetId = this.pendingAttachmentAssetId;
            }
          }
          if (this.pendingAudio) payload.audio = this.pendingAudio;
          this.els.input.value = '';
          storageSet(this.draftKey, null);
          this.pendingAudio = null;
          this.clearPendingAttachment();
          this.sendMessage(payload);
        }

        sendMessage(payload) {
          if (this.busy) return;
          var self = this;
          var epoch = this.epoch;
          this.busy = true;
          this.showNotice('');

          /* The OBJECTS, not a count: rollback removes exactly these by
             identity. A positional tail-splice deleted real messages whenever
             a snapshot (which replaces `messages` wholesale) interleaved with
             a failing send — after the replacement the optimistic entries are
             no longer the tail, and the filter below is then a harmless no-op
             instead of a wrong splice. */
          var optimistic = [];
          function pushOptimistic(message) {
            self.messages.push(message);
            optimistic.push(message);
          }
          if (payload.text) {
            pushOptimistic({ role: 'customer', contentType: 'text', text: payload.text });
          }
          if (payload.image) {
            pushOptimistic({ role: 'customer', contentType: 'image', text: this.t('image_attached') });
          }
          if (payload.audio) {
            pushOptimistic({ role: 'customer', contentType: 'audio', text: this.t('voice_note') });
          }
          this.renderAll();

          this.ensureSession()
            .then(function () {
              return self.buildMessageBody(payload);
            })
            .then(function (formData) {
              return apiFetch(self.apiBase + '/sessions/' + encodeURIComponent(self.session.id) + '/messages', {
                method: 'POST',
                body: formData
              });
            })
            .then(function (res) {
              /* Start Over ran while this turn was in flight — the customer
                 abandoned it, a fresh session owns the widget now. Touch
                 nothing (busy included: the new session may already have its
                 own turn running). */
              if (self.epoch !== epoch) return;
              if ((res.status === 202 || res.status === 200) && res.data && res.data.ok) {
                if (res.data.session) self.session = res.data.session;
                if (res.data.reply && res.data.reply.text) {
                  self.messages.push({ role: 'bot', contentType: 'text', text: res.data.reply.text });
                }
                self.busy = false;
                if (res.data.generating && res.data.generating.assetId) {
                  self.startPolling(res.data.generating.assetId);
                } else {
                  self.refreshSnapshotQuietly();
                }
                self.renderAll();
                self.emitStatus();
                return;
              }
              throw self.errorFromResponse(res);
            })
            .catch(function (err) {
              /* Abandoned by Start Over (this includes the abort that Start
                 Over itself fires at an in-flight upload): no rollback, no
                 restored attachment, no error notice over the fresh chat. */
              if (self.epoch !== epoch) return;
              self.busy = false;
              /* Roll back the optimistic bubbles and restore the composer so
                 the customer can retry without retyping. By identity — see
                 the comment where they were pushed. (Voice notes aren't
                 restorable — the recording is gone — so only the bubble is
                 rolled back.) */
              self.messages = self.messages.filter(function (message) {
                return optimistic.indexOf(message) === -1;
              });
              if (payload.text && !self.els.input.value) {
                self.els.input.value = payload.text;
                storageSet(self.draftKey, payload.text);
              }
              if (payload.image) {
                // Keep any successful upload so the retry references it rather
                // than re-uploading the file — tagged with the session it
                // belongs to so it can never be replayed against a new one.
                self.setPendingAttachment(
                  payload.image,
                  payload.imageAssetId,
                  payload.imageAssetId && self.session ? self.session.id : null
                );
              }
              self.showNotice(
                err && err.userText ? err.userText : self.errorText(err),
                'error',
                err && err.status === 429
              );
              self.renderAll();
            });
        }

        /**
         * Build the multipart body for a message send. A reference photo takes
         * the direct-upload path (straight to the backend, bypassing the ~4.5 MB
         * proxy cap — backend ADR-0021) when the session advertises an
         * uploadUrl + token; the message then carries only `image_asset_id`.
         * Falls back to the inline `image` field when the photo fits the proxy
         * cap and the direct upload isn't available or fails. Returns a Promise
         * resolving to the FormData (rejects with an `err.userText` for display).
         */
        buildMessageBody(payload) {
          var self = this;
          var formData = new FormData();
          /* Sent on EVERY turn, not just at session creation. Switching the
             storefront language reloads the page, but the widget resumes the
             SAME stored session — so a creation-time locale left an Arabic
             shopper talking to an English bot for the rest of the chat. This
             is the display language only; the backend still mirrors whatever
             language the customer actually writes in. */
          if (this.locale) formData.append('locale', this.locale);
          /* Which package is in the cart RIGHT NOW, on every turn rather than
             at session creation: the shopper can change the variant mid-chat,
             and for a product whose variant selects the physical scene the
             mockup has to follow the cart, not the conversation. Silently
             skipped for products with no option steps (chocolate sends
             nothing and is unaffected). */
          var chosen = this.selectedChoice();
          if (chosen && chosen.value) formData.append('selected_option', chosen.value);
          if (payload.text) formData.append('text', payload.text);
          if (payload.audio) formData.append('audio', payload.audio, payload.audio.name || 'voice-note');

          if (!payload.image) return Promise.resolve(formData);

          var appendInline = function () {
            formData.append('image', payload.image, payload.image.name || 'image');
            return formData;
          };

          // A retry whose file was already uploaded — just reference the asset.
          // (Only valid for the session that minted it; handleSend drops it
          // otherwise, so reaching here means it still belongs to this session.)
          if (payload.imageAssetId) {
            formData.append('image_asset_id', payload.imageAssetId);
            return Promise.resolve(formData);
          }

          var fitsProxy = payload.image.size <= MAX_PROXY_IMAGE_BYTES;
          if (!this.canDirectUpload()) {
            // No direct path (feature off / old backend / misconfigured CORS):
            // inline only if it fits the proxy's much smaller cap.
            if (!fitsProxy) {
              /* Nothing can carry this file right now. Surface it loudly —
                 silent degradation to a 4 MB experience while both locales
                 advertise 50 MB is how this stays broken unnoticed. */
              if (window.console && console.warn) {
                console.warn(
                  '[studio-designer] direct upload unavailable (session.uploadUrl/uploadToken ' +
                    'missing) and the file exceeds the proxy cap — check ' +
                    'STUDIO_PUBLIC_UPLOAD_BASE_URL and STUDIO_UPLOAD_ALLOWED_ORIGINS.'
                );
              }
              var tooBig = new Error('image_too_large');
              tooBig.userText = self.sizeLimitText(false, MAX_PROXY_IMAGE_BYTES);
              return Promise.reject(tooBig);
            }
            return Promise.resolve(appendInline());
          }

          return this.uploadImageDirect(payload.image)
            .then(function (assetId) {
              payload.imageAssetId = assetId; // remember for a possible retry
              formData.append('image_asset_id', assetId);
              return formData;
            })
            .catch(function (err) {
              /* Fall back to the proxy only when the direct attempt failed for
                 transport-shaped reasons (offline, CORS, DNS, timeout). An
                 explicit backend rejection — throttled, cap reached, too large,
                 bad type — must NOT be retried through the proxy: it would
                 double the bandwidth and hit a backend that just said no. */
              var status = err && err.status;
              var transportish = !status || status === 0 || status >= 500;
              if (fitsProxy && transportish && !(err && err.aborted)) return appendInline();
              var wrapped = new Error('upload_failed');
              wrapped.status = status;
              wrapped.userText = self.uploadErrorText(err);
              throw wrapped;
            });
        }

        /** Size-limit copy with the real number interpolated (one source of truth). */
        sizeLimitText(direct, cap) {
          var key = direct ? 'error_image_too_large' : 'error_image_too_large_proxy';
          return this.t(key).replace('{{ size }}', formatMegabytes(cap));
        }

        /** Can this session use the direct-upload endpoint? */
        canDirectUpload() {
          return !!(this.session && this.session.uploadUrl && this.session.uploadToken);
        }

        /** Customer-facing copy for a failed direct upload, by cause. */
        uploadErrorText(err) {
          var status = err && err.status;
          if (err && err.aborted) return this.t('error_upload_cancelled');
          if (status === 429) return this.t('error_upload_busy');
          if (status === 409) return this.t('error_upload_cap');
          if (status === 413) return this.t('error_image_too_large');
          if (status === 400 || status === 415) return this.t('error_image_unsupported');
          return this.t('error_upload_failed');
        }

        /**
         * POST a reference photo straight to the backend's direct-upload
         * endpoint (session.uploadUrl), authorized by the session-scoped token.
         * Resolves the stored asset id.
         *
         * XMLHttpRequest rather than fetch(): only XHR exposes upload progress,
         * and a 50 MB photo on mobile data is a multi-minute operation that the
         * customer must be able to watch and cancel. It also gives us a real
         * timeout — fetch() has none, so a half-open socket (common on cellular
         * handoff) would hang the composer forever.
         *
         * On a 401 the session is refreshed once, and the retry only happens if
         * that actually produced a NEW token — otherwise we'd re-upload the whole
         * file to earn a second guaranteed 401.
         */
        uploadImageDirect(file) {
          var self = this;

          var attempt = function (retriedToken) {
            return new Promise(function (resolve, reject) {
              var xhr = new XMLHttpRequest();
              self.activeUpload = xhr;
              // Re-evaluate the lock now that an upload is in flight: the
              // handoff escape hatch is re-enabled for its duration.
              self.syncComposerLock();
              xhr.open('POST', self.session.uploadUrl, true);
              xhr.timeout = UPLOAD_TIMEOUT_MS;
              xhr.responseType = 'text';
              xhr.setRequestHeader('Authorization', 'Bearer ' + self.session.uploadToken);
              xhr.setRequestHeader('Accept', 'application/json');

              var failure = function (status, data) {
                var err = new Error('upload_failed');
                err.status = status;
                err.serverMessage = (data && data.message) || '';
                return err;
              };

              xhr.upload.onprogress = function (event) {
                if (event.lengthComputable && event.total > 0) {
                  self.setUploadProgress(Math.round((event.loaded / event.total) * 100));
                }
              };
              xhr.onload = function () {
                var data = null;
                try {
                  data = JSON.parse(xhr.responseText);
                } catch (e) {
                  data = null;
                }
                if (xhr.status === 201 && data && data.asset_id) {
                  resolve(data.asset_id);
                  return;
                }
                if (xhr.status === 401 && !retriedToken && self.session && self.session.id) {
                  var staleToken = self.session.uploadToken;
                  self
                    .fetchSnapshot(self.session.id)
                    .then(function (result) {
                      if (
                        result !== 'ok' ||
                        !self.canDirectUpload() ||
                        self.session.uploadToken === staleToken
                      ) {
                        reject(failure(401, data));
                        return;
                      }
                      resolve(attempt(true));
                    })
                    .catch(function () {
                      reject(failure(401, data));
                    });
                  return;
                }
                reject(failure(xhr.status, data));
              };
              xhr.onerror = function () {
                reject(failure(0, null)); // transport-shaped: offline, CORS, DNS
              };
              xhr.ontimeout = function () {
                reject(failure(0, null));
              };
              xhr.onabort = function () {
                var err = new Error('upload_cancelled');
                err.aborted = true;
                reject(err);
              };

              var fd = new FormData();
              fd.append('image', file, file.name || 'image');
              xhr.send(fd);
            });
          };

          this.setUploadProgress(0);
          return attempt(false).then(
            function (assetId) {
              self.clearUploadProgress();
              return assetId;
            },
            function (err) {
              self.clearUploadProgress();
              throw err;
            }
          );
        }

        /** Render upload progress + a Cancel affordance into the notice area. */
        setUploadProgress(percent) {
          this.uploadProgress = percent;
          var self = this;
          var label = this.t('uploading').replace('{{ percent }}', String(percent));
          this.els.notice.textContent = '';
          this.els.notice.hidden = false;
          this.els.notice.className = 'studio-chat__notice studio-chat__notice--progress';
          var text = document.createElement('span');
          text.textContent = label;
          this.els.notice.appendChild(text);
          var cancel = document.createElement('button');
          cancel.type = 'button';
          cancel.className = 'studio-chat__notice-cancel';
          cancel.textContent = this.t('cancel');
          cancel.addEventListener('click', function () {
            self.cancelUpload();
          });
          this.els.notice.appendChild(cancel);
        }

        clearUploadProgress() {
          this.uploadProgress = null;
          this.activeUpload = null;
          if (this.els && this.els.notice) this.showNotice('');
          this.syncComposerLock();
        }

        /** Abort an in-flight upload (Cancel button / element teardown). */
        cancelUpload() {
          if (this.activeUpload) {
            var xhr = this.activeUpload;
            this.activeUpload = null;
            xhr.abort();
          }
        }

        refreshSnapshotQuietly() {
          if (!this.session || !this.session.id) return;
          var self = this;
          this.fetchSnapshot(this.session.id).then(function (result) {
            if (result === 'ok') {
              self.renderAll();
              self.emitStatus();
            }
          });
        }

        /* ---------------- generation polling ---------------- */

        resumePollingIfGenerating() {
          var self = this;
          (this.assets || []).forEach(function (asset) {
            if (asset.status === 'GENERATING') self.startPolling(asset.id);
          });
        }

        startPolling(assetId) {
          this.stopPolling();
          this.pollingAssetId = assetId;
          this.pollTimedOutAssetId = null;
          this.pollTicks = 0;
          var self = this;
          this.renderAll();
          this.pollTimer = setInterval(function () {
            if (!self.session || !self.session.id) return;
            self.pollTicks += 1;
            if (self.pollTicks > POLL_MAX_TICKS) {
              /* Past the backend's own ceiling, so nothing is still coming.
                 Surface a FAILED-style card with a retry row instead of
                 stranding the UI on "generating" forever. */
              var timedOutId = self.pollingAssetId;
              self.stopPolling();
              self.pollTimedOutAssetId = timedOutId;
              self.renderAll();
              self.emitStatus();
              return;
            }
            self.fetchSnapshot(self.session.id).then(function (result) {
              if (result !== 'ok') return;
              var asset = null;
              (self.assets || []).forEach(function (candidate) {
                if (candidate.id === self.pollingAssetId) asset = candidate;
              });
              if (asset && (asset.status === 'READY' || asset.status === 'FAILED')) {
                self.stopPolling();
              }
              self.renderAll();
              self.emitStatus();
            });
          }, POLL_INTERVAL_MS);
        }

        stopPolling() {
          if (this.pollTimer) clearInterval(this.pollTimer);
          this.pollTimer = null;
          this.pollingAssetId = null;
        }

        /* ---------------- approvals / handoff / start over ---------------- */

        postAction(path, body) {
          var self = this;
          if (!this.session || !this.session.id) return Promise.reject(new Error('no_session'));
          if (this.busy) {
            /* A tap that raced an in-flight turn is a double-submit, not a
               failure — the typing indicator already says "working". This
               used to share the no_session rejection and told the customer
               "Something went wrong" for pressing a visible button twice.
               `silent` tells every catch handler to drop it without a notice. */
            var busyErr = new Error('busy');
            busyErr.silent = true;
            return Promise.reject(busyErr);
          }
          var epoch = this.epoch;
          this.busy = true;
          this.showNotice('');
          this.renderAll();
          return apiFetch(this.apiBase + '/sessions/' + encodeURIComponent(this.session.id) + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body || {})
          }).then(function (res) {
            /* Abandoned by Start Over mid-flight: the new session may already
               be running its own turn, so clearing `busy` here could unlock a
               double-send. Leave all state to the epoch that owns it. */
            if (self.epoch !== epoch) {
              var stale = new Error('stale');
              stale.silent = true;
              throw stale;
            }
            self.clearBusy();
            if (res.status >= 200 && res.status < 300 && res.data && res.data.ok) return res.data;
            throw self.errorFromResponse(res);
          }).catch(function (err) {
            if (self.epoch === epoch) self.clearBusy();
            throw err;
          });
        }

        /**
         * Drop the busy flag AND repaint, because `busy` is drawn.
         *
         * Setting it puts "Twinkle Bot is typing…" in the chat (see
         * `collectTypingRow`), so clearing it has to take that bubble away in
         * the same breath. It used to be a bare assignment, which left every
         * caller that did not happen to re-render the MESSAGE list with a
         * typing indicator that never stopped — tapping a design in the picker
         * was one: `saveSelection` repaints the slots row and nothing else, so
         * the bot appeared to be typing for the rest of the session (USER,
         * 2026-09-06: "why the Twinkle Bot is typing… is stuck").
         *
         * Owned here rather than fixed at each call site: whoever turns the
         * indicator on is who must turn it off.
         */
        clearBusy() {
          this.busy = false;
          this.renderAll();
        }

        /**
         * Post the package the picker now holds, and show the mockup composed
         * onto it.
         *
         * Debounced, because the theme fires `variant:change` on its own
         * renders as well as the shopper's clicks, and each real switch costs
         * an image composition on the server. Deduped against the last value
         * we sent for the same reason.
         *
         * Silent on failure: this is a picture catching up, and the design,
         * the price and the buy button are all correct either way. A red
         * error over the chat for something the customer did not ask for is
         * worse than a mockup that arrives a beat late.
         */
        pushChosenOption() {
          var self = this;
          var chosen = this.selectedChoice();
          var value = chosen && chosen.value;
          if (!value || !this.session || !this.session.id) return;
          if (value === this.lastPushedOption) return;
          this.lastPushedOption = value;
          if (this.optionPushTimer) clearTimeout(this.optionPushTimer);
          this.optionPushTimer = setTimeout(function () {
            var epoch = self.epoch;
            var sessionId = self.session && self.session.id;
            if (!sessionId) return;
            apiFetch(self.apiBase + '/sessions/' + encodeURIComponent(sessionId) + '/option/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ option: value })
            })
              .then(function (res) {
                /* Abandoned by Start Over mid-flight — the reply belongs to a
                   session that no longer exists. */
                if (self.epoch !== epoch) return;
                if (res.status < 200 || res.status >= 300) return;
                /* The compose finished server-side before the response
                   returned, so the new mockup is in this snapshot. */
                self.refreshSnapshotQuietly();
              })
              .catch(function () {
                /* Leave the previous mockup standing. */
              });
          }, 400);
        }

        handleApproveUnit() {
          var self = this;
          this.postAction('/approve-unit')
            .then(function (data) {
              if (data.session) self.session = data.session;
              /* Two-phase: the returned asset is the composed layout preview —
                 poll it to READY. Single-approval: it's the (already READY)
                 approved unit; the session is final, just refresh. */
              if (data.approvedAssetId && self.hasLayoutPhase()) {
                self.startPolling(data.approvedAssetId);
              }
              self.refreshSnapshotQuietly();
              self.renderAll();
              self.emitStatus();
            })
            .catch(function (err) {
              if (err && err.silent) return;
              self.showNotice(self.errorText(err), 'error');
              self.renderAll();
            });
        }

        handleApproveLayout() {
          var self = this;
          this.postAction('/approve-layout', { piece_count: this.pieceCountForSelection() })
            .then(function (data) {
              if (data.session) self.session = data.session;
              self.stopPolling();
              self.refreshSnapshotQuietly();
              self.renderAll();
              self.emitStatus();
            })
            .catch(function (err) {
              if (err && err.silent) return;
              self.showNotice(self.errorText(err), 'error');
              self.renderAll();
            });
        }

        /** Retry from a FAILED (or poll-timed-out) asset card. The timeout
         *  flag is cleared by startPolling() once a new generation actually
         *  begins — clearing it here would revert the card to a dead
         *  "generating" state if the retry send itself fails. */
        handleRetryGeneration() {
          this.sendMessage({ text: this.t('retry_message') });
        }

        /**
         * Ask the ATC block to add the approved design to the bag.
         *
         * Deliberately an event rather than a direct call: <studio-designer-atc>
         * is a sibling block with no shared parent, and it owns the whole
         * purchase path (server re-validation, the one-designed-item-per-order
         * rule, the linkage line properties, the cart-drawer refresh). Copying
         * any of that here would give us two implementations to keep in sync,
         * and the one in the chat would be the one nobody remembered to update.
         */
        requestAddToBag() {
          document.dispatchEvent(new CustomEvent('studio-designer:add-to-bag', { bubbles: true }));
        }

        handleHandoff(phone, eventDate) {
          var self = this;
          /* FLAT { phone }, not { customer: { phone } }. The app proxy accepts
             the flat shape and re-nests it into the backend's contract itself
             (twinkle-tools proxy.delivery-assignment.studio.sessions.$id.handoff).
             Sending the backend's shape here means the proxy silently reads no
             phone and the shopper stays anonymous — which is what happened. */
          var body = phone ? { phone: phone } : {};
          /* The date is the whole reason a rush handoff exists — a designer is
             being asked "can you build this by the 3rd?" — and it was the one
             thing the payload did not carry. It lives on the buy block's field,
             not in the chat, so a shopper who filled the date, was refused and
             tapped straight through handed the designer an empty session and no
             date at all.

             Sent as `note`, which the app proxy already forwards and the
             backend already folds into the handoff brief. No contract change
             on either side — the field was there. */
          if (eventDate) {
            /* English on purpose, and NOT a t() key: this sentence is read by
               the design team in DWS, not by the shopper. Localising it to the
               storefront would mean the brief's language depended on which
               language the customer happened to be browsing in. */
            body.note = 'Requested event date: ' + eventDate +
              ' — inside the lead time. The customer chose to talk to a designer' +
              ' rather than pick a later date.';
          }
          /* The handoff outranks an in-flight upload. syncComposerLock keeps
             this button alive during one precisely so a multi-minute upload
             can never trap the customer — but `busy` spans the whole upload
             turn and postAction rejects on it, so the escape hatch dead-ended
             with a generic error in the one scenario it exists for. Abandon
             the upload instead: abort it (onabort fires synchronously, so the
             turn's rejection fully unwinds `busy` within its microtasks) and
             give those microtasks one macrotask to drain before posting. */
          this.cancelUpload();
          new Promise(function (resolve) { setTimeout(resolve, 0); })
            .then(function () {
              return self.ensureSession();
            })
            .then(function () {
              return self.postAction('/handoff', body);
            })
            .then(function (data) {
              if (data.session) self.session = data.session;
              /* The aborted upload's rollback re-staged its photo; a pending
                 attachment has no meaning in a handed-off session. */
              self.clearPendingAttachment();
              self.lastWhatsappUrl = data.whatsappUrl || null;
              if (data.whatsappUrl) {
                var opened = window.open(data.whatsappUrl, '_blank', 'noopener');
                if (!opened) {
                  /* popup blocked — the handed-off state renders the link */
                }
              } else {
                /* No number configured on the backend, so nothing opens — and
                   the session is now handed off, which B7.5 makes one-way. The
                   shopper tapped a button that promised a person and got a
                   stopped chat, which is worse than the refusal they started
                   with. Say what happened and give them the number to use. */
                self.showNotice(self.t('handoff_no_link'), 'info');
              }
              self.stopPolling();
              self.renderAll();
              self.emitStatus();
            })
            .catch(function (err) {
              if (err && err.silent) return;
              self.showNotice(self.errorText(err), 'error');
              self.renderAll();
            });
        }

        /**
         * A second design, additively.
         *
         * Mechanically this is Start Over — a fresh session, an empty chat —
         * but it is the opposite thing to the customer, and offering it under
         * that name was the whole problem: the only route to a second tray
         * was a button whose confirm dialog asks whether they want to discard
         * their design. People who wanted two trays read that and stopped.
         *
         * So: no confirm (there is nothing to lose — the design they made is
         * paid-for state in the bag, not in this session), and a notice that
         * says so before they wonder where it went.
         */
        handleDesignAnother() {
          this.handleStartOver('another_started');
          var reduceMotion =
            window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        }

        handleStartOver(noticeKey) {
          var self = this;
          /* First: everything async that belongs to the old session is now
             stale. The bump makes in-flight sendMessage/postAction callbacks
             drop their results — including the abort below, whose rejection
             would otherwise re-stage the old attachment and paint an "upload
             cancelled" error over the fresh chat. */
          this.epoch += 1;
          storageSet(this.sessionKey, null);
          this.session = null;
          this.messages = [];
          this.assets = [];
          this.lastWhatsappUrl = null;
          this.stopPolling();
          /* Start Over is the customer's way out of a stuck state, so it must
             clear EVERYTHING — an in-flight upload and any pending attachment
             included. Leaving the asset id behind would replay the old session's
             upload into the new session, which the backend rejects on every
             send, trapping the chat in a loop Start Over can't escape. */
          this.cancelUpload();
          this.clearPendingAttachment();
          this.pendingAudio = null;
          this.busy = false;
          this.ensureSession()
            .then(function () {
              self.renderAll();
              /* After the repaint, so nothing in it can land on top. */
              if (noticeKey) self.showNotice(self.t(noticeKey));
              self.emitStatus();
            })
            .catch(function (err) {
              self.showNotice(self.errorText(err), 'error');
              self.renderAll();
              self.emitStatus();
            });
        }

        /* ---------------- voice notes ---------------- */

        /**
         * Say out loud what the mic is doing: 'pending' while the browser is
         * asking for permission, 'recording' with a running clock, 'idle'
         * otherwise. The button's colour was the only signal before, and the
         * tester who tried it read the change as nothing at all — "I just
         * assumed I have to tap it again once I'm done, which is correct, but
         * there needs to be some feedback." role="status" also announces it,
         * which is the only cue a screen-reader user ever had.
         */
        showRecordingState(state) {
          this.stopRecordingTimer();
          var el = this.els.recording;
          if (!el) return;
          if (state === 'idle') {
            this.micPending = false;
            el.hidden = true;
            el.textContent = '';
            el.classList.remove('studio-chat__recording--live');
            return;
          }
          el.hidden = false;
          if (state === 'pending') {
            el.classList.remove('studio-chat__recording--live');
            el.textContent = this.t('mic_pending');
            return;
          }
          el.classList.add('studio-chat__recording--live');
          this.recordingStartedAt = Date.now();
          this.renderRecordingElapsed();
          var self = this;
          this.recordingTimer = window.setInterval(function () {
            self.renderRecordingElapsed();
          }, 1000);
        }

        /** "0:07" — a clock the shopper can watch, so the mode is unmistakable. */
        renderRecordingElapsed() {
          if (!this.els.recording || !this.recordingStartedAt) return;
          var total = Math.max(0, Math.floor((Date.now() - this.recordingStartedAt) / 1000));
          var seconds = total % 60;
          var clock = Math.floor(total / 60) + ':' + (seconds < 10 ? '0' : '') + seconds;
          this.els.recording.textContent = fillToken(this.t('mic_recording'), '{{ time }}', clock);
        }

        stopRecordingTimer() {
          if (this.recordingTimer) {
            window.clearInterval(this.recordingTimer);
            this.recordingTimer = null;
          }
          this.recordingStartedAt = null;
        }

        startRecording() {
          var self = this;
          if (isMicBusy(this.recording, this.micPending)) return;
          /* Before getUserMedia, not after: on a first recording the permission
             prompt is the longest part of the interaction and nothing on the
             page moved while it was open. A second tap during that wait used
             to start a second recorder; micPending holds the lock until idle. */
          this.micPending = true;
          this.showRecordingState('pending');
          navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then(function (stream) {
              var mimeType = '';
              if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported('audio/webm')) {
                mimeType = 'audio/webm';
              } else if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported('audio/mp4')) {
                mimeType = 'audio/mp4';
              }
              self.recordedChunks = [];
              self.mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType: mimeType }) : new MediaRecorder(stream);
              self.mediaRecorder.addEventListener('dataavailable', function (event) {
                if (event.data && event.data.size > 0) self.recordedChunks.push(event.data);
              });
              self.mediaRecorder.addEventListener('stop', function () {
                var type = self.mediaRecorder.mimeType || 'audio/webm';
                var extension = type.indexOf('mp4') !== -1 ? 'm4a' : 'webm';
                var blob = new Blob(self.recordedChunks, { type: type });
                self.stopRecorderTracks();
                self.recording = false;
                self.els.mic.classList.remove('studio-chat__mic--recording');
                self.els.mic.setAttribute('aria-label', self.t('record_voice'));
                self.showRecordingState('idle');
                if (blob.size > 0) {
                  self.pendingAudio = new File([blob], 'voice-note.' + extension, { type: type });
                  self.sendMessage({ audio: self.pendingAudio, text: self.els.input.value.trim() });
                  self.pendingAudio = null;
                  self.els.input.value = '';
                  storageSet(self.draftKey, null);
                }
              });
              self.mediaRecorder.start();
              self.recording = true;
              self.els.mic.classList.add('studio-chat__mic--recording');
              self.els.mic.setAttribute('aria-label', self.t('stop_recording'));
              self.showRecordingState('recording');
            })
            .catch(function () {
              /* permission denied / unsupported — hide the mic gracefully */
              self.showRecordingState('idle');
              self.els.mic.hidden = true;
            });
        }

        stopRecording() {
          if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop();
          }
        }

        stopRecorderTracks() {
          if (this.mediaRecorder && this.mediaRecorder.stream) {
            this.mediaRecorder.stream.getTracks().forEach(function (track) {
              track.stop();
            });
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Gated Add-to-Bag — reads the theme variant picker, gated by chat    */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('studio-designer-atc')) {
    customElements.define(
      'studio-designer-atc',
      class StudioDesignerAtc extends HTMLElement {
        connectedCallback() {
          this.apiBase = this.dataset.apiBase || '/apps/delivery-assignment/studio';
          this.productSlug = this.dataset.productSlug || '';
          this.variants =
            readJsonScript(this, '[data-studio-variants]') ||
            readJsonScript(document, '[data-studio-variants]') ||
            [];
          this.i18n = readJsonScript(document, '[data-studio-i18n]') || {};
          this.chatStatus = null;
          this.readyToOrder = false;
          this.sessionId = null;
          this.previewAssetId = null;
          this.adding = false;
          this.counted = false;
          /* Designed lines for THIS product already in the bag. 0 until the
             cart is read, so nothing is claimed before it is known. */
          this.designedInBag = 0;
          this.bagSessionIds = [];
          this.currentVariantId =
            parseInt(this.dataset.initialVariantId, 10) ||
            (this.variants[0] && this.variants[0].id) ||
            null;

          this.atcButton = this.querySelector('[data-studio-atc]');
          this.atcLabel = this.querySelector('[data-studio-atc-label]');
          this.atcHint = this.querySelector('[data-studio-atc-hint]');
          this.atcFacts = this.querySelector('[data-studio-atc-facts]');
          this.atcError = this.querySelector('[data-studio-atc-error]');
          this.atcRush = this.querySelector('[data-studio-atc-rush]');
          this.atcWarning = this.querySelector('[data-studio-atc-warning]');
          this.atcSwitch = this.querySelector('[data-studio-atc-switch]');
          this.atcNote = this.querySelector('[data-studio-atc-note]');
          this.atcAnother = this.querySelector('[data-studio-atc-another]');
          this.atcInBag = this.querySelector('[data-studio-atc-in-bag]');
          this.readEventField();
          /* 0 until the config arrives, which is also the value for every
             product with no event date — the field stays hidden either way. */
          this.leadTimeDays = 0;
          /* The store's today, from the backend. '' until the config arrives,
             which falls back to the browser's date — see today(). */
          this.shopToday = '';
          this.mismatch = null;

          var self = this;

          if (this.atcRush) {
            this.atcRush.addEventListener('click', function () {
              document.dispatchEvent(
                new CustomEvent('studio-designer:rush', {
                  bubbles: true,
                  /* The date they were refused for, travelling WITH the
                     request. The chat element owns the handoff and this one
                     owns the field, and the designer being asked "can you
                     build this by the 3rd?" needs the date more than anything
                     else in the payload. */
                  detail: { eventDate: self.rushEventDate() }
                })
              );
            });
          }

          /* Chat state bubbles to document — chat and ATC are sibling blocks now. */
          this.onChatStatus = function (event) {
            self.chatStatus = event.detail.status;
            /* Take the slug the chat actually booted with rather than our own
               attribute: both are rendered from the same metafield, so if a
               translation app overwrote it the chat has already recovered it
               and this is how the cart line gets the recovered value too. */
            if (event.detail.productSlug) self.productSlug = event.detail.productSlug;
            self.readyToOrder = !!event.detail.readyToOrder;
            self.sessionId = event.detail.sessionId;
            self.previewAssetId = event.detail.previewAssetId;
            self.pieceCount = event.detail.pieceCount || null;
            self.pieceMm = event.detail.pieceMm || null;
            self.choiceNote = event.detail.choiceNote || '';
            self.productName = event.detail.productName || '';
            self.counted = !!event.detail.counted;
            self.leadTimeDays = event.detail.leadTimeDays || 0;
            self.packagingNote = event.detail.packagingNote || '';
            self.shopToday = event.detail.shopToday || '';
            self.mismatch = event.detail.optionMismatch || null;
            self.syncAtc();
            /* The customer asked the bot for a different package, so move the
               picker (and the price, and their bag) rather than render a
               button asking them to. `syncAtc` first, so the offer is on
               screen if the switch itself cannot be made. */
            if (self.mismatch && self.mismatch.autoSwitch && self.mismatch.variantId) {
              self.switchToDiscussed();
            }
          };
          document.addEventListener('studio-designer:status', this.onChatStatus);

          /* The chat found no usable config for this PDP — hide the gated ATC
             and reveal the theme's standard buy buttons (the snippet's hiding
             CSS is scoped on the section NOT having .studio-fallback). */
          this.onFallback = function () {
            var section = self.closest('[id^="shopify-section-"]');
            if (section) section.classList.add('studio-fallback');
            self.hidden = true;
          };
          document.addEventListener('studio-designer:fallback', this.onFallback);

          /* The approved-state card in the chat asks us to buy on its behalf,
             so the purchase path has exactly one implementation. Guarded the
             same way the button is: an add already in flight, or a session
             that isn't ready, must not be started twice from a second entry
             point (handleAddToBag re-validates against the server regardless). */
          this.onAddToBagRequest = function () {
            if (self.adding) return;
            self.handleAddToBag();
          };
          document.addEventListener('studio-designer:add-to-bag', this.onAddToBagRequest);

          /* "Pick another date" in the rush confirm. The modal lives in the
             chat element and the field lives here, so the way back is an
             event — and it has to actually land on the input: dismissing a
             dialog and leaving the shopper looking at an unchanged page is
             how the same tap happens again. */
          this.onFocusEventDate = function () {
            if (!self.eventInput) return;
            self.eventInput.focus();
            self.eventInput.scrollIntoView({
              behavior: window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
                ? 'auto'
                : 'smooth',
              block: 'center'
            });
          };
          document.addEventListener('studio-designer:focus-event-date', this.onFocusEventDate);

          /* Selected variant comes from the theme's variant picker. */
          this.onVariantChange = function (event) {
            if (event.detail && event.detail.variant) {
              self.currentVariantId = event.detail.variant.id;
              self.syncAtc();
            }
          };
          document.addEventListener('variant:change', this.onVariantChange);

          /* The bag can change where this element cannot see it — the cart
             page in another tab, a line removed and the browser sent back
             here. Re-reading on return is what keeps the count from claiming
             a design the shopper has since taken out. */
          this.onVisible = function () {
            if (!document.hidden) self.refreshBagCount();
          };
          document.addEventListener('visibilitychange', this.onVisible);

          if (this.atcButton) {
            this.atcButton.addEventListener('click', function () {
              self.handleAddToBag();
            });
          }

          /* The second tray. The chat owns the session, so this only asks. */
          if (this.atcAnother) {
            this.atcAnother.addEventListener('click', function () {
              document.dispatchEvent(
                new CustomEvent('studio-designer:design-another', { bubbles: true })
              );
            });
          }

          /* One tap from "these two disagree" to "the price says what the bot
             said". Deliberately a button and not an automatic correction: the
             variant picker is the shopper's, and silently moving what someone
             is about to pay for is the one thing worse than the mismatch. */
          if (this.atcSwitch) {
            this.atcSwitch.addEventListener('click', function () {
              self.switchToDiscussed();
            });
          }

          /* Answer the date the moment it is picked — both ways.

             Clearing a stale error is the obvious half: one still showing
             under a corrected field reads as "still wrong".

             RAISING one is the half that was missing, and it is the whole of
             the mobile complaint: "on desktop it greys out the dates... but on
             mobile I can click on everything. Let me pick September 7th, 8th —
             it says nothing" (founder, 2026-09-06). The `min` attribute is
             advisory (see `syncEventDate`), and desktop browsers happen to
             honour it by drawing those days as unclickable. iOS renders the
             same input as a scroll wheel with no disabled state, so nothing
             stopped the date being chosen and nothing said a word until Add to
             Bag — after the whole backdrop had been designed.

             Nothing was ever purchasable with a bad date; `eventDateError()`
             at Add to Bag and the server both refused it. What was broken was
             WHEN the shopper found out.

             Only when a value is actually present: `eventDateError()` also
             reports an empty field, and telling someone their date is missing
             at the instant they clear it is nagging, not helping. Add to Bag
             still catches that case. */
          if (this.eventInput) {
            this.eventInput.addEventListener('change', function () {
              /* RAISED at the field, since R7 moved the date up to its own
                 step and the Add-to-Bag error line is a screen and a half
                 below it — an answer down there is no answer.

                 CLEARED in both places, because either may be showing this
                 same error: Add to Bag reports at the button, and an error
                 still standing under a field the shopper has just corrected
                 reads as "still wrong". */
              var dateError = self.eventInput.value ? self.eventDateError() : '';
              self.showEventError(dateError);
              if (!dateError) self.showAtcError('');
              else self.syncRush();
            });
          }

          this.syncAtc();
          this.refreshBagCount();
        }

        disconnectedCallback() {
          if (this.onChatStatus) {
            document.removeEventListener('studio-designer:status', this.onChatStatus);
          }
          if (this.onFallback) {
            document.removeEventListener('studio-designer:fallback', this.onFallback);
          }
          if (this.onAddToBagRequest) {
            document.removeEventListener('studio-designer:add-to-bag', this.onAddToBagRequest);
          }
          if (this.onFocusEventDate) {
            document.removeEventListener('studio-designer:focus-event-date', this.onFocusEventDate);
          }
          if (this.onVariantChange) {
            document.removeEventListener('variant:change', this.onVariantChange);
          }
          if (this.onVisible) {
            document.removeEventListener('visibilitychange', this.onVisible);
          }
        }

        /**
         * Same `{{ product }}` slot as the chat's `t()`. This element has no
         * config of its own — the name rides in on the status event — but the
         * copy it renders is the same generic copy, and a raw "{{ product }}"
         * printed under the buy button is worse than the tray wording it
         * replaced.
         */
        t(key) {
          var text = this.i18n[key] || key;
          if (text.indexOf('{{ product }}') !== -1) {
            text = fillToken(
              text,
              '{{ product }}',
              this.productName || this.i18n['product_fallback_name'] || 'design'
            );
          }
          return text;
        }

        selectedVariant() {
          if (!this.variants.length) return null;
          var id = this.currentVariantId;
          var match = null;
          this.variants.forEach(function (variant) {
            if (variant.id === id) match = variant;
          });
          return match || this.variants[0];
        }

        /**
         * How many designed lines of THIS product are already in the bag.
         *
         * Read from the cart rather than remembered, because the customer's
         * bag outlives this tab: they add a tray, close the page, come back a
         * day later, and the offer to design another has to be true on that
         * visit too. It also has to survive the other direction — a line
         * removed from the drawer must stop being counted — and the cart is
         * the only thing that knows either.
         *
         * Scoped by `_studio_product` so a backdrop in the bag does not make
         * a chocolate PDP claim the shopper has a chocolate tray waiting.
         * Failure is silent on purpose: the count is an invitation, and a
         * cart request that did not come back is no reason to break the buy
         * button next to it.
         */
        refreshBagCount() {
          var self = this;
          return fetch(window.Shopify.routes.root + 'cart.js', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          })
            .then(function (response) {
              return response.json();
            })
            .then(function (cart) {
              self.cartAttributes = cart.attributes || {};
              self.bagSessionIds = (cart.items || [])
                .filter(function (item) {
                  var props = item.properties || {};
                  if (!props._design_session_id) return false;
                  /* An older line carries no slug — count it here rather than
                     lose it: on a studio PDP a designed line with no product
                     of its own is far more likely to be this product than
                     not. */
                  if (!props._studio_product || !self.productSlug) return true;
                  return props._studio_product === self.productSlug;
                })
                .map(function (item) {
                  return item.properties._design_session_id;
                });
              self.designedInBag = self.bagSessionIds.length;
              self.syncAtc();
            })
            .catch(function () {
              /* Leave the count as it was; nothing here is worth an error. */
            });
        }

        /**
         * The two things a bag with a design in it has to say.
         *
         * The LINE — "1 design already in your bag" — is there because the
         * chat resets to an empty conversation when a second design starts,
         * and an empty chat is exactly what a lost design looks like. It
         * shows whenever the bag holds one, including on a fresh visit.
         *
         * The BUTTON only shows once the design in THIS tab is in the bag.
         * Offering it while an approved-but-unadded design is on screen would
         * be offering to discard it, with no confirm and no way back — the
         * precise thing Start Over asks about and this deliberately does not.
         */
        syncBagLine() {
          if (this.atcInBag) {
            var count = this.designedInBag;
            var text =
              count <= 0
                ? ''
                : fillToken(
                    this.t(count === 1 ? 'in_bag_one' : 'in_bag_many'),
                    '{{ count }}',
                    String(count)
                  );
            this.atcInBag.textContent = text;
            this.atcInBag.hidden = !text;
          }
          if (this.atcAnother) {
            var thisOneIsBought =
              !!this.sessionId && this.bagSessionIds.indexOf(this.sessionId) !== -1;
            this.atcAnother.textContent = this.t('design_another');
            this.atcAnother.hidden = !thisOneIsBought;
          }
        }

        syncAtc() {
          this.syncEventDate();
          this.syncBagLine();
          if (!this.atcButton) return;
          var variant = this.selectedVariant();
          var approved = this.readyToOrder;
          var enabled = !!variant && variant.available && approved && !this.adding;

          if (variant && this.atcLabel && !this.adding) {
            this.atcLabel.textContent = this.t('add_to_bag') + ' — ' + variant.price_formatted;
          }

          /* Styled-disabled, not attribute-disabled. A `disabled` button fires
             no click event, so the shopper who taps a greyed-out "Add to bag"
             — the single most common confusion in testing — cannot be told
             why. Keeping it focusable lets us answer; `aria-disabled` keeps
             the semantics honest and handleAddToBag() re-checks the same
             condition, so nothing can be bought early. The one exception is
             an add already in flight: that button must be inert. */
          this.atcButton.disabled = !!this.adding;
          this.atcButton.setAttribute('aria-disabled', enabled ? 'false' : 'true');

          /* Piece count sits WITH the price, where the shopper is deciding —
             every tester who asked for it was looking at the buy button, not
             the description. Same number that sizes the print sheet.
             Rendered ABOVE the button, not below it: "I was about to add to
             cart and thought, where's the information about the box? I scrolled
             and saw it underneath — but nobody reads below Add to Bag."

             The packaging sentence names a Twinkle box, which is a claim about
             THIS product. The studio engine is being generalised, so it is
             gated on the config resolving a piece count — the marker of a
             product with the chocolate-style quantity map. A future product
             that ships in something else stays quiet rather than promising
             packaging it does not use. */
          if (this.atcFacts) {
            var self = this;
            var parts = productFacts(
              function (key) { return self.t(key); },
              this.pieceCount,
              this.pieceMm,
              this.choiceNote,
              this.counted,
              this.packagingNote
            );
            this.atcFacts.textContent = parts.join(' ');
            this.atcFacts.hidden = parts.length === 0;
          }

          if (this.atcHint) {
            if (variant && !variant.available) {
              this.atcHint.textContent = this.t('error_unavailable');
              this.atcHint.hidden = false;
            } else if (!approved) {
              this.atcHint.textContent = this.t('atc_hint');
              this.atcHint.hidden = false;
            } else {
              this.atcHint.hidden = true;
            }
          }
        }

        /**
         * Today as the STORE reckons it, `YYYY-MM-DD`.
         *
         * The lead time is the team's working days in Riyadh, and the Shopify
         * checkout validation judges it against the shop's own date. This used
         * to come from `new Date()` — the SHOPPER's date — so anyone west of us
         * late in the day was offered a date the widget called valid and
         * checkout then refused, with no way back: the widget still thought the
         * date was fine, so it never offered the "talk to a designer" way out
         * either.
         *
         * Falls back to the browser's day when the backend sent nothing, which
         * is where this started and is right often enough for a Saudi store.
         */
        today() {
          if (/^\d{4}-\d{2}-\d{2}$/.test(this.shopToday || '')) return this.shopToday;
          var now = new Date();
          return (
            now.getFullYear() +
            '-' +
            String(now.getMonth() + 1).padStart(2, '0') +
            '-' +
            String(now.getDate()).padStart(2, '0')
          );
        }

        /**
         * The earliest date we can build for, as `YYYY-MM-DD`.
         *
         * Calendar arithmetic on the store's date, done through UTC so a
         * daylight-saving boundary in the browser's zone cannot move the answer
         * — the parts go in as UTC and come straight back out, so no local
         * offset is ever applied.
         */
        earliestEventDate() {
          var parts = this.today().split('-');
          var day = new Date(
            Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
          );
          day.setUTCDate(day.getUTCDate() + (this.leadTimeDays || 0));
          return (
            day.getUTCFullYear() +
            '-' +
            String(day.getUTCMonth() + 1).padStart(2, '0') +
            '-' +
            String(day.getUTCDate()).padStart(2, '0')
          );
        }

        /**
         * Why this date cannot be ordered, or '' when it can.
         *
         * A string compare is enough and safer than parsing: both sides are
         * `YYYY-MM-DD`, which sorts lexicographically, and `new Date(value)`
         * would drag the browser's timezone back into a question that is
         * purely about calendar days.
         */
        /**
         * Find the event-date field, which renders OUTSIDE this element.
         *
         * It moved up to sit above the chat as its own step (R7) — asking for
         * the date after the whole design was the complaint — but the rule
         * that decides whether the order can be taken has to stay in one
         * place, and that place is here, with the lead time and Add to Bag.
         * So the markup moved and the logic did not.
         *
         * Scoped to the section rather than the document, the same way
         * `markSizeStep` scopes its radios: a collection page or a "goes well
         * with" slider can put a second studio widget on the page, and an
         * element that grabbed the first matching input anywhere would drive
         * another product's field.
         */
        readEventField() {
          var root = this.closest('[id^="shopify-section-"]') || document;
          this.eventField = root.querySelector('[data-studio-event-field]');
          this.eventStep = root.querySelector('[data-studio-event-step]');
          this.eventLabel = root.querySelector('[data-studio-event-label]');
          this.eventInput = root.querySelector('[data-studio-event-input]');
          this.eventHint = root.querySelector('[data-studio-event-hint]');
          this.eventError = root.querySelector('[data-studio-event-error]');
        }

        eventDateError() {
          if (!this.leadTimeDays) return '';
          /* A lead time with no field is a BROKEN PAGE, not a pass.
             `''` here means "allow the purchase", and until R7 that branch was
             unreachable: the input was declared in this element's own snippet,
             so an element that existed had a field. The field now lives in the
             chat block, so a template rendering `studio_atc` without
             `studio_chat` would otherwise lose the lead-time rule AND the
             "Event date" line-item property silently — an order that never
             says when the party is, with nothing anywhere to notice it. */
          if (!this.eventInput) {
            this.warnMissingEventField();
            return this.t('event_date_missing');
          }
          var value = (this.eventInput.value || '').trim();
          if (!value) return this.t('event_date_missing');
          var earliest = this.earliestEventDate();
          if (value < earliest) {
            return fillToken(this.t('event_date_too_soon'), '{{ date }}', earliest);
          }
          return '';
        }

        /**
         * Whether the date is filled in but earlier than we can build for —
         * as opposed to simply blank.
         *
         * The two need different answers. A blank field is a step the shopper
         * has not done yet and they can just do it. A date under the lead time
         * is a "no" they cannot fix by trying again, and ops removed rush
         * orders entirely (2026-08-20), so the only next step we can offer is
         * a person: "Need it sooner? Talk to a designer" (`atc.rush_cta`).
         */
        eventDateTooSoon() {
          if (!this.leadTimeDays || !this.eventInput) return false;
          var value = (this.eventInput.value || '').trim();
          return !!value && value < this.earliestEventDate();
        }

        /**
         * The date field, the mismatch warning and the handcrafted note —
         * everything that only applies to a product built and installed to a
         * customer's date. Every one of them is inert (and hidden) for a
         * config with no lead time, which is every product but the backdrop.
         */
        syncEventDate() {
          var wanted = this.leadTimeDays > 0;
          if (this.eventField) this.eventField.hidden = !wanted;
          if (wanted && this.eventInput) {
            /* `min` is advisory — a native picker greys earlier days out, but
               a typed or scripted value still arrives. eventDateError() is
               what actually gates the purchase. */
            this.eventInput.min = this.earliestEventDate();
            /* "Step 2" over the field, matching the picker's "Step 1" above and
               the chat's "Step 3" below. Stamped here rather than in Liquid
               for the same reason as the rest of this copy: the field only
               exists for a product with a lead time, and only this code knows
               whether it has one. */
            if (this.eventStep && !this.eventStep.firstChild) {
              var step = document.createElement('span');
              step.className = 'studio-step-lead__step';
              step.textContent = this.t('step_two');
              this.eventStep.appendChild(step);
            }
            if (this.eventLabel) this.eventLabel.textContent = this.t('event_date_label');
            if (this.eventHint) {
              this.eventHint.textContent = fillToken(
                this.t('event_date_hint'),
                '{{ days }}',
                localizeDigits(this.leadTimeDays)
              );
            }
          }
          if (this.atcNote) {
            this.atcNote.textContent = wanted ? this.t('handcrafted_note') : '';
            this.atcNote.hidden = !wanted;
          }
          if (this.atcWarning) {
            var mismatch = this.mismatch;
            if (mismatch && mismatch.discussed && mismatch.selected) {
              this.atcWarning.textContent = fillToken(
                fillToken(this.t('option_mismatch'), '{{ discussed }}', mismatch.discussed),
                '{{ selected }}',
                mismatch.selected
              );
              this.atcWarning.hidden = false;
            } else {
              this.atcWarning.textContent = '';
              this.atcWarning.hidden = true;
            }
          }
          if (this.atcSwitch) {
            var offer = this.mismatch;
            /* No target variant (sold out, or a spec value the config cannot
               resolve) leaves the warning standing on its own. */
            var canSwitch = !!(offer && offer.discussed && offer.variantId && (offer.options || []).length);
            if (canSwitch) {
              /* The price is IN the button. "Switch to balloons one side" asks
                 the shopper to accept a number nobody has shown them; the whole
                 complaint was about the number. */
              this.atcSwitch.textContent = fillToken(
                fillToken(this.t('option_switch'), '{{ discussed }}', offer.discussed),
                '{{ price }}',
                offer.price
              );
            }
            this.atcSwitch.hidden = !canSwitch;
          }
        }

        /**
         * Move the theme's variant picker onto the package the bot settled on.
         *
         * Drives the REAL picker — checks its radios and lets it fire its own
         * `change` — rather than setting `currentVariantId` here. Everything
         * downstream of a variant (the price block, the URL, the media, our own
         * label, and the `selected_option` the chat posts with the next turn)
         * hangs off the theme's `variant:change`, and a local-only update would
         * have left the button saying one price while the page said another.
         *
         * Scoped to this product's section: the studio template also mounts
         * recommendations and a collection slider, each with their own
         * `.product-form__input` radios.
         */
        switchToDiscussed() {
          var offer = this.mismatch;
          if (!offer || !offer.variantId || !(offer.options || []).length) return;
          var root = this.closest('[id^="shopify-section-"]') || document;
          var picker = root.querySelector('variant-radios, variant-selects');
          if (!picker) return;

          /* Positional, exactly as the theme reads them: group N holds
             option N (VariantRadios walks `fieldset`s, VariantSelects walks
             `select`s, both by index). Matching by value across the whole
             picker would cross groups on a two-option product.

             Both shapes are handled because the studio engine is being
             generalised: a product configured with a dropdown picker would
             otherwise get a button that silently does nothing. */
          var last = null;
          var groups = picker.querySelectorAll('fieldset');
          if (groups.length) {
            for (var i = 0; i < groups.length && i < offer.options.length; i += 1) {
              var wanted = String(offer.options[i]);
              var inputs = groups[i].querySelectorAll('input[type="radio"]');
              for (var j = 0; j < inputs.length; j += 1) {
                if (String(inputs[j].value) !== wanted) continue;
                if (!inputs[j].checked) {
                  inputs[j].checked = true;
                  last = inputs[j];
                }
                break;
              }
            }
          } else {
            var selects = picker.querySelectorAll('select');
            for (var s = 0; s < selects.length && s < offer.options.length; s += 1) {
              var want = String(offer.options[s]);
              if (selects[s].value === want) continue;
              /* Only when the option really exists: assigning an unknown value
                 silently blanks a <select>, leaving the picker in a state no
                 variant matches. Compared in JS rather than through a
                 querySelector — an option value can carry quotes. */
              var options = selects[s].options;
              var hit = false;
              for (var k = 0; k < options.length; k += 1) {
                if (String(options[k].value) === want) hit = true;
              }
              if (!hit) continue;
              selects[s].value = want;
              last = selects[s];
            }
          }
          /* One dispatch, not one per fieldset: the theme re-reads every
             fieldset on any change, so a second event would only re-run the
             same work (and re-render the product info block again). */
          if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
          /* The picker and the price now say the new package. A bag that
             still holds the old one would say otherwise at checkout, which is
             the disagreement this whole affordance exists to end. */
          this.moveCartLine(offer.variantId);
        }

        /**
         * Move this design's line onto ``variantId``, if it is already in the bag.
         *
         * The shopper can add to bag and keep talking — "actually, make it both
         * sides" — and until now that left them paying for the package they
         * had left while the page, the mockup and the brief all showed the new
         * one. USER, 2026-08-31: *"the bot must change the package price in the
         * product page AND the cart if the customer clicked on add to cart"*.
         *
         * Shopify has no change-the-variant call: `/cart/change.js` moves
         * quantity and properties, never the variant. So the line is re-added
         * on the new variant and the old one zeroed — in that order, because
         * an add that fails after a removal would leave the customer with no
         * design in the bag at all, which is worse than a stale package.
         *
         * Silent when there is nothing to move, which is the ordinary case:
         * most shoppers settle the package before they ever reach the bag.
         */
        moveCartLine(variantId) {
          var self = this;
          if (!variantId || !this.sessionId) return Promise.resolve();
          return fetch(window.Shopify.routes.root + 'cart.js', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          })
            .then(function (response) {
              return response.json();
            })
            .then(function (cart) {
              var line = (cart.items || []).filter(function (item) {
                return item.properties && item.properties._design_session_id === self.sessionId;
              })[0];
              if (!line || String(line.variant_id) === String(variantId)) return null;
              /* The line's OWN properties, not freshly built ones: the event
                 date and the preview link were settled when it was added, and
                 rebuilding them here would drop the date if the field has
                 since been cleared. */
              return fetch(window.routes.cart_add_url + '.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                  items: [{ id: variantId, quantity: line.quantity || 1, properties: line.properties }]
                })
              })
                .then(function (response) {
                  return response.json();
                })
                .then(function (added) {
                  if (added && added.status) {
                    /* Sold out, or the variant went away. The old line is
                       untouched — say so rather than leave them to find the
                       old package at checkout. */
                    throw Object.assign(new Error('cart_move_failed'), {
                      userText: added.description || self.t('error_generic')
                    });
                  }
                  return fetch(window.routes.cart_change_url + '.js', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ id: line.key, quantity: 0 })
                  });
                })
                .then(function () {
                  return self.refreshCartDrawer();
                });
            })
            .catch(function (err) {
              self.showAtcError((err && err.userText) || self.t('error_generic'));
            });
        }

        /**
         * Re-render the drawer so the bag on screen matches the bag we hold.
         *
         * Only while it is ALREADY open. `renderContents` opens the drawer as
         * well as filling it (the theme uses it for add-to-cart, where opening
         * is the point), and this move can run on page load — so without the
         * guard, arriving at the product page threw the bag open in the
         * shopper's face for a change they never asked to see. Closed, it
         * needs nothing: the quantity has not changed, so the header count is
         * still right, and the drawer reads the cart when it opens.
         */
        refreshCartDrawer() {
          var drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          if (!drawer || !drawer.getSectionsToRender || !drawer.renderContents) return null;
          if (!drawer.classList.contains('active') && !drawer.classList.contains('animate')) return null;
          var sections = drawer.getSectionsToRender().map(function (section) {
            return section.id;
          });
          return fetch(
            window.Shopify.routes.root +
              '?sections=' +
              encodeURIComponent(sections.join(',')) +
              '&section_url=' +
              encodeURIComponent(window.location.pathname),
            { credentials: 'same-origin', headers: { Accept: 'application/json' } }
          )
            .then(function (response) {
              return response.json();
            })
            .then(function (data) {
              drawer.renderContents({ sections: data });
            })
            .catch(function () {
              /* The bag itself is already right; only its rendering is stale,
                 and the next drawer open re-reads it. Never fail the move. */
            });
        }

        /**
         * The line-item properties that carry this design onto the order.
         *
         * A method of its own so it can be exercised without a cart, a fetch
         * and a variant: these four strings are the entire contract between
         * this widget and everything downstream, and each of them is read by
         * something in another repo that will not notice a rename.
         *
         *  ``_design_session_id``  the order webhook attaches the design by it
         *  ``_studio_product``     twinkle-tools resolves the quantity map
         *  ``Event date``          twinkle-tools forwards it; the backend puts
         *                          it on the brief and flags a lead-time
         *                          breach; Shopify's checkout validation reads
         *                          `attribute(key: "Event date")` and CANNOT
         *                          match it case-insensitively
         *  ``Design preview``      the clickable preview for CX and the kitchen
         *
         * The two `_`-prefixed ones are hidden from the customer; the other two
         * are deliberately not, so they show on the order and the packing slip.
         * "Event date" is the day the setup has to be STANDING and is a
         * different thing from the checkout-wide "Shipment Date".
         */
        cartLineProperties() {
          var properties = { _design_session_id: this.sessionId };
          if (this.productSlug) {
            /* The product config slug — the tools webhook resolves the
               variant→quantity map from it (ADR-0022 / D7). */
            properties._studio_product = this.productSlug;
          }
          var eventDate = this.eventInput && (this.eventInput.value || '').trim();
          if (this.leadTimeDays > 0 && eventDate) {
            properties['Event date'] = eventDate;
          }
          if (this.previewShortCode) {
            var shortBase = this.apiBase.replace(/\/studio$/, '') + '/s/';
            properties['Design preview'] = window.location.origin + shortBase + this.previewShortCode;
          } else if (this.previewAssetId) {
            properties['Design preview'] =
              window.location.origin + this.apiBase + '/assets/' + this.previewAssetId;
          }
          /* The tile itself, for the cart's thumbnail (W1). Underscore-hidden:
             this is ours, not a field the customer is being shown, and it
             must not reach notification emails or packing slips the way a
             named property would. The cart templates fall back to
             `Design preview` when it is absent, so a line added before this
             shipped still shows artwork. */
          if (this.thumbShortCode) {
            properties._design_thumb =
              window.location.origin + this.apiBase.replace(/\/studio$/, '') + '/s/' + this.thumbShortCode;
          }
          return properties;
        }

        /** The date the shopper was refused for, or '' when the field is empty. */
        rushEventDate() {
          return this.eventInput ? (this.eventInput.value || '').trim() : '';
        }

        /**
         * Say so when the date field is not on the page but should be.
         *
         * The gate above refuses the purchase rather than passing it, which
         * protects the order — but a shopper who cannot see a date field and
         * is told their date is missing has an unwinnable page, so this has to
         * reach whoever can fix the template. Once per element.
         */
        warnMissingEventField() {
          if (this.warnedMissingEventField) return;
          this.warnedMissingEventField = true;
          if (!window.console || !console.warn) return;
          console.warn(
            '[studio-designer] this product has a ' +
              this.leadTimeDays +
              '-day lead time but no event-date field is on the page, so no date ' +
              'can be collected and Add to Bag will refuse. The field is rendered ' +
              'by the studio CHAT block (studio-designer-chat.liquid) and read by ' +
              'this one — add that block back to the product template.'
          );
        }

        /** The date's own error line, beside the field it is about. */
        showEventError(text) {
          if (!this.eventError) return;
          this.eventError.textContent = text || '';
          this.eventError.hidden = !text;
        }

        showAtcError(text) {
          if (this.atcError) {
            this.atcError.textContent = text || '';
            this.atcError.hidden = !text;
          }
          this.syncRush();
        }

        /**
         * The escape hatch, shown only where it is the actual next step.
         *
         * Two conditions, both required: there is an error on screen, and the
         * date really is the too-soon one rather than a blank field. A blank
         * field is a step the shopper has not done yet and can just do.
         *
         * This used to claim a third — "the backend gave us somewhere to send
         * them" — left behind when the WhatsApp-link contract was reverted on
         * the tools side. The code never checked it, and the case the sentence
         * was afraid of is handled in `handleHandoff` instead: a handoff that
         * comes back with no `whatsappUrl` says so rather than parking the
         * session and opening nothing.
         */
        syncRush() {
          if (!this.atcRush) return;
          /* Either surface. The pick-time error lands on the field (R7) and
             the Add-to-Bag one lands at the button; "Need it sooner? Talk to a
             designer" is the ONLY sanctioned way round a too-soon date (ops
             2026-08-20: no rush orders at all; USER 2026-08-23: if needed then
             talk to the designer), so it has to appear for both. Reading only
             `atcError` meant it stopped appearing at the moment it is most
             needed — the moment the date is picked. */
          var shown =
            (!!this.atcError && !this.atcError.hidden) ||
            (!!this.eventError && !this.eventError.hidden);
          var show = this.eventDateTooSoon() && shown;
          if (show) this.atcRush.textContent = this.t('rush_cta');
          this.atcRush.hidden = !show;
        }

        /**
         * The design isn't approved yet, so there is nothing to buy. Testers
         * read the grey button as "I've missed a step" and could not work out
         * which — the required step is the bot above, which several of them
         * hadn't recognised as a design tool at all. Say so, then take them to it.
         */
        explainNotReady() {
          this.showAtcError('');
          if (this.atcHint) this.atcHint.hidden = false;
          var chat = document.querySelector('studio-designer-chat');
          var block = (chat && chat.closest('.studio-chat-block')) || chat;
          if (!block) return;
          var reduceMotion =
            window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          block.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          block.classList.remove('studio-chat-block--nudge');
          /* Reflow so the class re-application restarts the animation even when
             the shopper taps the button repeatedly. */
          void block.offsetWidth;
          block.classList.add('studio-chat-block--nudge');
        }

        /** Bring this block into view — the caller may be the chat card above. */
        revealSelf() {
          var reduceMotion =
            window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        }

        handleAddToBag() {
          if (this.adding) return;
          /* Every rejection below has to SAY something: this can now be
             triggered from the approved card up in the chat, and a button
             that silently does nothing is the bug this whole change set is
             about. */
          var variant = this.selectedVariant();
          if (!variant || !variant.available) {
            this.showAtcError(this.t('error_unavailable'));
            this.revealSelf();
            return;
          }
          /* Not approved yet — the disabled case the shopper just clicked
             through. Explain it and point at the step they're missing. */
          if (!this.readyToOrder) {
            this.explainNotReady();
            return;
          }
          if (!this.sessionId) {
            this.showAtcError(this.t('error_generic'));
            this.revealSelf();
            return;
          }
          /* No "too many designs" refusal here any more. It used to block the
             bag when more designs were approved than the size could carry —
             one per kilo. A tray carries one design now, the picker chooses
             which, and approving five alternatives is ordinary use rather than
             something to apologise for at the moment they press buy. */
          /* The event date, for products built to one. Checked here rather
             than only in the field, because the approved card up in the chat
             buys through this same method and its button is nowhere near the
             date — so the shopper is shown the error AND scrolled to the
             field they have to fill.

             This is the 5-day rule's only client-side enforcement and it is
             bypassable (issue 12): the brief flags anything that gets through,
             which is why the backend carries the same number. */
          var dateError = this.eventDateError();
          if (dateError) {
            this.showAtcError(dateError);
            this.revealSelf();
            if (this.eventInput) this.eventInput.focus();
            return;
          }

          var self = this;
          this.adding = true;
          this.showAtcError('');
          var originalLabel = this.atcLabel ? this.atcLabel.textContent : '';
          if (this.atcLabel) this.atcLabel.textContent = this.t('adding');
          this.syncAtc();

          var finish = function () {
            self.adding = false;
            if (self.atcLabel && originalLabel) self.atcLabel.textContent = originalLabel;
            self.syncAtc();
          };

          /* 1. Re-validate against server truth — never trust client state.
             The final status depends on the flow shape: LAYOUT_APPROVED for
             two-phase products, UNIT_APPROVED for single-approval ones (the
             session echoes its product config, so the check is server-driven). */
          apiFetch(this.apiBase + '/sessions/' + encodeURIComponent(this.sessionId))
            .then(function (res) {
              var session = res.status === 200 && res.data && res.data.ok ? res.data.session : null;
              var twoPhase = !session || !session.product || session.product.hasLayoutPhase !== false;
              var finalStatus = twoPhase ? 'LAYOUT_APPROVED' : 'UNIT_APPROVED';
              if (!session || session.status !== finalStatus) {
                throw Object.assign(new Error('not_approved'), { userText: self.t('error_not_approved') });
              }
              /* Refresh the preview asset from the fresh snapshot. */
              var assets = res.data.assets || [];
              var layout = null;
              var unitPreview = null;
              var unit = null;
              assets.forEach(function (asset) {
                if (asset.status !== 'READY') return;
                if (asset.kind === 'LAYOUT_PREVIEW') layout = asset;
                if (asset.kind === 'UNIT_PREVIEW') unitPreview = asset;
                if (asset.kind === 'UNIT_DESIGN') unit = asset;
              });
              var previewAsset = layout || unitPreview || unit;
              self.previewAssetId = (previewAsset && previewAsset.id) || self.previewAssetId;
              /* Short base62 handle for the compact preview URL; null for
                 pre-launch assets (then we fall back to the long asset URL). */
              self.previewShortCode = (previewAsset && previewAsset.shortCode) || null;
              /* The ARTWORK, separately, for the cart's thumbnail (W1).
                 `Design preview` resolves to the tray mockup where there is
                 one, which is the right thing for the staff link — but at 56
                 px in the drawer, two mockups of the same tray are the same
                 picture, so the line that was supposed to tell two designs
                 apart told them apart by nothing. The tile does. */
              var thumbAsset = unit || unitPreview || layout;
              self.thumbShortCode = (thumbAsset && thumbAsset.shortCode) || null;

              /* 2. One designed item per order (v1 rule). */
              return fetch(window.Shopify.routes.root + 'cart.js', {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' }
              }).then(function (response) {
                return response.json();
              });
            })
            .then(function (cart) {
              /* Kept for the backup-linkage step: existing cart attributes. */
              self.cartAttributes = cart.attributes || {};
              /* One design per TRAY, not per order (D4, answered 2026-08-09:
                 "would be nice to carry more than one design per order if
                 that's what they want"). Each design is its own line item with
                 its own piece count and print sheet, which is how fulfilment
                 already works — so a second tray is a second line, and Start
                 over is how the customer begins it.
                 What is still blocked is adding the SAME design twice: that is
                 a double-submit, not a second tray, and it would bill the
                 customer twice for one design. */
              var alreadyInBag = (cart.items || []).some(function (item) {
                return item.properties && item.properties._design_session_id === self.sessionId;
              });
              if (alreadyInBag) {
                throw Object.assign(new Error('one_per_order'), { userText: self.t('error_one_per_order') });
              }

              /* 3. Add the line with linkage properties.
                 _design_session_id stays hidden — it's what the order webhook
                 reads to attach the design. "Design preview" is the clickable
                 preview URL shown on the order for CX/kitchen staff (checkout
                 auto-links it). The short code is a bearer capability — the
                 unguessable code IS the credential, with no customer binding —
                 so the link opens for staff regardless of the shopper's login
                 state. The long-asset-URL fallback only fires for pre-launch
                 sessions with no short code; it resolves for the shopper but
                 may not for staff. */
              var cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
              var properties = self.cartLineProperties();
              var body = {
                items: [{ id: variant.id, quantity: 1, properties: properties }]
              };
              if (cartDrawer && cartDrawer.getSectionsToRender) {
                body.sections = cartDrawer.getSectionsToRender().map(function (section) {
                  return section.id;
                });
                body.sections_url = window.location.pathname;
              }
              return fetch(window.routes.cart_add_url + '.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(body)
              }).then(function (response) {
                return response.json().then(function (data) {
                  return { cartDrawer: cartDrawer, data: data };
                });
              });
            })
            .then(function (result) {
              if (result.data.status) {
                /* Shopify /cart/add.js error payload */
                throw Object.assign(new Error('cart_add_failed'), {
                  userText: result.data.description || self.t('error_generic')
                });
              }
              /* 4. Backup linkage: hidden cart attribute → order
                 note_attributes. `_`-prefixed so Shopify keeps it off
                 customer-facing surfaces (the raw session id is a read
                 credential for anonymous sessions — it must not leak into
                 notification emails / packing slips), and list-valued
                 (comma-separated, appended) so a bypassed one-per-order check
                 can't silently overwrite an earlier design's linkage. The
                 tools webhook reads `_studio_design_sessions` (and still
                 honours the chocolate-era attributes from in-flight carts). */
              var existingList = (self.cartAttributes && self.cartAttributes._studio_design_sessions) || '';
              var sessions = String(existingList).split(',').map(function (s) {
                return s.trim();
              }).filter(Boolean);
              if (sessions.indexOf(self.sessionId) === -1) sessions.push(self.sessionId);
              var attributes = { _studio_design_sessions: sessions.join(',') };
              /* Migrate away from the chocolate-era attributes: empty string
                 deletes them from the cart. */
              ['_chocolate_design_sessions', 'Chocolate Design Session'].forEach(function (legacy) {
                if (self.cartAttributes && self.cartAttributes[legacy]) {
                  attributes[legacy] = '';
                }
              });
              return fetch(window.routes.cart_update_url + '.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ attributes: attributes })
              }).then(function () {
                return result;
              }).catch(function () {
                /* attribute write is a backup — don't fail the add */
                return result;
              });
            })
            .then(function (result) {
              finish();
              /* The bag just gained a line, so the offer to design another —
                 and the count that reassures them the first one is safe —
                 both change here. Re-read rather than increment: the drawer
                 that opens next can remove the line, and a remembered number
                 would go on claiming it. */
              self.refreshBagCount();
              if (result.cartDrawer && result.cartDrawer.renderContents && result.data.sections) {
                result.cartDrawer.renderContents(result.data);
              } else {
                window.location = window.routes.cart_url;
              }
            })
            .catch(function (err) {
              finish();
              self.showAtcError((err && err.userText) || self.t('error_generic'));
            });
        }
      }
    );
  }
})();
